import { reportBelongsToSite, siteForReport } from '../../../lib/reportSiteIdentity';
/**
 * FeasibilityRerunPanel — resolve data gaps, adjust config, and rerun report
 * generation. Creates a new version linked to the parent report.
 *
 * This is the default panel + the base for the per-type sibling panels (which
 * thinly wrap this and pass overrides). Workflow Prompt 6.
 *
 * Override props:
 *   - gapCategorizer: groups data gaps under domain-specific buckets so
 *     per-type panels can show e.g. "Grid capacity" / "Renewables" instead
 *     of a flat list. Defaults to feasibility's single-bucket "Other".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../../store';
import { STABLE_EMPTY_RECORD } from '../../../store/stableEmpty';
import { useGenerationHeartbeat } from '../../../store/generationHeartbeat';
import { useUrlSearchParamSync } from '../../../lib/useUrlSearchParamSync';
import type { SiteRagReport, ProjectIntent, TypedPreferencesByType } from '../../../types/siteRag';
import type { ReportTypeId } from '../../../config/reportTypes';
import { validatePreferencesFor } from '../preferences';
import { RerunProviderDataStatus } from './RerunProviderDataStatus';
import { RerunFreshnessList } from './RerunFreshnessList';
import { computeSedSectionStatuses } from '../../../lib/sedStaleness';
import { REPORT_TYPE_SED_REQUIREMENTS } from '../../../config/reportTypeSedRequirements';
import { preferencesFromIntent } from '../../../lib/intentToPreferences';
import { GenerationProgress } from '../GenerationProgress';
import { SentimentEvidenceHoldNotice, useSentimentEvidenceHold } from '../SentimentEvidenceNotice';
import { SentimentScanRefreshOption } from './SentimentScanRefreshOption';
import {
  SENTIMENT_EVIDENCE_WAITING_NOTICE,
  publicSentimentEvidenceDisposition,
} from '../../../lib/sentiment/sentimentEvidenceReadiness';
import { useGenerationBeforeUnloadGuard } from '../../../lib/useGenerationBeforeUnloadGuard';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { X, AlertTriangle, Upload, FileText, ChevronRight, RefreshCw, Loader2, CheckCircle2, Key, Send, Zap } from 'lucide-react';
import { DiffReviewDialog } from '../DiffReviewDialog';
import { toast } from 'sonner';
import { getGapCategoriesForType, type GapCategoryGroup } from '../../../lib/rerunGapCategories';
// UFL — shared upload validator. Replaces the inline 10 MB / VALID_EXTS
// duplicates at the rerun-wizard upload paths.
import {
  classifyUploadFileList,
  describeUploadRejection,
  summarizeRejections,
} from '../../../lib/uploadValidation';
// UFL-5 — one telemetry event per rejected upload (soft-fail).
import { emitUploadRejected } from '../../../lib/auditLogClient';
import { effectiveWorkloadProfile, deriveUseCase } from '../../../lib/workloadProfile';
import { REPORT_TYPE_REGISTRY } from '../../../config/reportTypes';
import { workloadProfileLabel } from '../WorkloadProfilePicker';
import { RerunConfigurationStep } from './RerunConfigurationStep';
import { classifyGap, type ApiProviderDef } from '../../../config/apiProviders';
import { TestAndLinkKeyDialog } from '../TestAndLinkKeyDialog';
import { getRerunDefaults } from '../../../lib/rerunDefaults';
// PreflightFreshnessCard mount removed from this panel in Reader
// Polish Prompt 8 — replaced by RerunFreshnessList + a single
// footer CTA. The shared card still ships in ReportPreferencesStep
// for the wizard's pre-generation freshness gate.
import type { SedSectionId, SiteEvidenceDocument } from '../../../types/siteEvidence';
import { extractSEDVariables } from '../../../services/sedAmendments';
import {
  ResolveAmendmentDialog,
  type ResolveAmendmentDraft,
} from '../decision/ResolveAmendmentDialog';
import { emitAuditEvent } from '../../../lib/auditLogClient';
import { computeRerunDelta, isRerunDeltaEmpty } from '../../../lib/rerunDelta';
import { RerunDeltaCard } from './RerunDeltaCard';
import type { ApiProviderId } from '../../../config/apiProviders';
import { API_PROVIDERS } from '../../../config/apiProviders';
import { ProviderPostureRow, type ProviderPostureItem } from './ProviderPostureRow';
import { computeRerunPreflight, deriveProviderPostureFromReport } from '../../../lib/rerunPreflight';
import { RerunDiffChip } from './RerunDiffChip';
import { computeRerunDiff } from '../../../lib/rerunDiff';
import { generateVersionNote } from '../../../lib/rerunVersionNote';
import { suggestProjectIntent } from '../../../lib/suggestProjectIntent';
// SRW-2 (2026-05-19) — the inline editable `SiteContextInput` mount was
// retired; the rerun-step now renders a read-only summary card with a
// deep-link to the site Context tab. The `SiteContextEditMode` type
// remains imported because `siteContextMode` state is still threaded
// through `handleRerun` for back-compat with historical 'rerun-override'
// callers (dead UI today, but the type union is part of the persisted
// audit-log breadcrumb shape).
import type { SiteContextEditMode } from '../SiteContextInput';
import type { SiteContext } from '../../../types/siteContext';
import { emptySiteContext } from '../../../types/siteContext';
// SCR (Site Configuration Stickiness on Rerun, row 0.99291) — freshest-wins
// resolver shared across the three site-config surfaces the wizard initialises
// from a snapshot (projectIntent, siteContext, typedPreferences). The pack
// fixes the read-init staleness bug where the wizard ignored edits the user
// made on the Site Overview tab AFTER the source report was generated.
import { resolveFreshestSiteConfig, type ScrSource } from '../../../lib/site/resolveFreshestSiteConfig';
import {
  isRichProjectIntent,
  projectIntentRichnessScore,
} from '../../../lib/site/projectIntentRichness';
import { validateCustomBriefPreferences } from '../preferences/CustomBriefPreferences';

export interface DataGapItem {
  field: string;
  description: string;
  impact: 'critical' | 'significant' | 'minor';
  resolved: boolean;
  ignored: boolean;
  resolution?: string;
  /**
   * Canonical provider id forwarded from the SED gap so
   * `classifyGap` can do exact-match routing instead of substring
   * scanning the description (Content-Source Fixes Prompt 10).
   */
  providerId?: string;
}

export interface RerunPanelProps {
  report: SiteRagReport;
  open: boolean;
  onClose: () => void;
  /** Override the gap-bucket categorizer. Falls back to feasibility's "Other" bucket. */
  gapCategorizer?: (gaps: DataGapItem[]) => GapCategoryGroup<DataGapItem>[];
}

/**
 * Async report jobs commit their report metadata before the terminal job is
 * marked succeeded, but the client-side report subscription can still trail
 * that commit briefly. Keep the retry window bounded so a broken hydration
 * path becomes a visible error instead of an endless "Finalizing" panel.
 */
const ASYNC_REPORT_HYDRATION_MAX_ATTEMPTS = 20;
const ASYNC_REPORT_HYDRATION_RETRY_MS = 1_000;

/**
 * Read-time derivation shim for Step 3's typed preferences.
 * Rerun Wizard Coherence W5.
 *
 * Step 3 (typed prefs) is the source of truth for the fields that used to
 * have bucketed Step-2 dropdowns (projectSizeMW / powerRequirementMW,
 * coolingPreference / coolingType, inServiceDate / timeToPowerCriticalDate).
 * Legacy reports written before typedPreferences persistence carry only a
 * `rerunConfig.projectIntent`, so when the panel mounts we:
 *
 *   1. take whatever typed prefs are persisted (rerunConfig override →
 *      top-level fallback),
 *   2. derive a prefs object from the parent's ProjectIntent via the
 *      existing `*FromIntent` mappers,
 *   3. layer the persisted values ON TOP of the derived ones — so a field
 *      the user actually set wins, but any field left unset is back-filled
 *      from intent rather than opening empty (and immediately invalid).
 *
 * Exported so the W5 unit test can target it without rendering the panel.
 */
export function deriveInitialRerunPrefs<T extends ReportTypeId>(
  report: SiteRagReport,
  reportType: T,
): NonNullable<TypedPreferencesByType[T]> {
  const defaults = getRerunDefaults(report);
  const seeded = (defaults.typedPreferences?.[reportType]
    ?? report.typedPreferences?.[reportType]) as TypedPreferencesByType[T] | undefined;
  const fromIntent = preferencesFromIntent(reportType, report.rerunConfig?.projectIntent);
  // `*FromIntent` returns a `compact()`-ed Partial<...> (undefined keys
  // already dropped), so a plain spread merge does the right thing:
  // derived first, persisted last.
  return { ...(fromIntent ?? {}), ...(seeded ?? {}) } as NonNullable<TypedPreferencesByType[T]>;
}

/** Human label for a typed-pref key in the review diff / auto-version-note.
 *  SSOT — the eight retired field labels are kept so reruns of pre-SSOT
 *  reports (whose `typedPreferencesAtGen` snapshots still carry the
 *  deleted keys as dead bytes) can render a legible diff. New rerun
 *  configs never write these keys; the labels are read-only legacy. */
const TYPED_PREF_LABELS: Record<string, string> = {
  projectSizeMW: 'Project size (MW)',          // SSOT-SENTINEL-OK — historical-diff label
  powerRequirementMW: 'Power requirement (MW)', // SSOT-SENTINEL-OK — historical-diff label
  coolingPreference: 'Cooling preference',     // SSOT-SENTINEL-OK — historical-diff label
  coolingType: 'Cooling type',                 // SSOT-SENTINEL-OK — historical-diff label
  inServiceDate: 'In-service date',
  timeToPowerCriticalDate: 'Time-to-power date',
  customerCategory: 'Customer category',       // SSOT-SENTINEL-OK — historical-diff label
  riskTolerance: 'Risk tolerance',             // SSOT-SENTINEL-OK — historical-diff label
  powerSourcePreference: 'Power source',       // SSOT-SENTINEL-OK — historical-diff label
  btmStrategy: 'BTM strategy',
  renewablesPreference: 'Renewables',
  curtailmentRiskTolerance: 'Curtailment risk',
  expectedNepaPathway: 'NEPA pathway',
  airBasinOverride: 'Air basin',
  dischargeWaterTarget: 'Discharge target',
  federalNexus: 'Federal nexus',
  tribalLandProximityKnown: 'Tribal land proximity',
  droughtScenario: 'Drought scenario',
  wastewaterTarget: 'Wastewater target',
  waterRightsKnown: 'Water rights',
  targetFootprintAcres: 'Target footprint (ac)',
  phasing: 'Phasing',
  phaseCount: 'Phase count',
  setbackOverrideFt: 'Setback (ft)',
  parkingRatioPerEmployee: 'Parking ratio',
  stormwaterDetentionGal: 'Stormwater detention (gal)',
  carrierDiversityMin: 'Carrier diversity (min)',
  bandwidthGbps: 'Bandwidth (Gbps)',
  ixPeeringRequired: 'IX peering required',
  submarineCableProximityImportant: 'Submarine-cable proximity',
  kpiWeightOverrides: 'KPI weight overrides',
  speciesConcerns: 'Species concerns',
  latencyTargetsMs: 'Latency targets',
};

function labelForTypedPref(key: string): string {
  return TYPED_PREF_LABELS[key] ?? key;
}

function formatTypedPrefValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

export interface TypedPrefDelta { key: string; label: string; from: string; to: string }

function normalizeCustomBriefPrefs(
  value: TypedPreferencesByType['custom-brief'],
): TypedPreferencesByType['custom-brief'] {
  const questions = (value?.questions ?? [])
    .map((q, idx) => ({
      id: q.id || `q${idx}`,
      text: q.text.trim(),
    }))
    .filter((q) => q.text.length > 0);
  return {
    ...value,
    briefName: value?.briefName?.trim() ?? '',
    objective: value?.objective?.trim() ?? '',
    questions,
  };
}

/**
 * Diff two typed-preference objects, keyed union of both, skipping unchanged
 * fields. Object-valued fields (kpiWeightOverrides, latencyTargetsMs) are
 * compared by JSON shape. Used by the Step-4 review diff AND the
 * auto-version-note (Rerun Wizard Coherence W6).
 *
 * Exported for the W8 integration test.
 */
export function typedPrefDeltas(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined,
): TypedPrefDelta[] {
  const a = before ?? {};
  const b = after ?? {};
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const deltas: TypedPrefDelta[] = [];
  for (const key of keys) {
    const av = a[key];
    const bv = b[key];
    const same = typeof av === 'object' || typeof bv === 'object'
      ? JSON.stringify(av ?? null) === JSON.stringify(bv ?? null)
      : av === bv;
    if (same) continue;
    deltas.push({ key, label: labelForTypedPref(key), from: formatTypedPrefValue(av), to: formatTypedPrefValue(bv) });
  }
  return deltas;
}

/**
 * RP2 — best-effort map a rerun data gap to a concrete SED variable so its
 * value can be resolved in-place. CONSERVATIVE: returns null unless a SED
 * variable's domain/providerId or label clearly matches the gap — a fabricated
 * path would be rejected by `applySingleAmendmentToSed`'s schema guard. Pure.
 */
function matchSedVariableForGap(
  sed: SiteEvidenceDocument,
  gap: DataGapItem,
): { path: string; label: string; domain: string; value: string | number | boolean | null; unit?: string } | null {
  let vars: ReturnType<typeof extractSEDVariables>;
  try {
    vars = extractSEDVariables(sed);
  } catch {
    return null;
  }
  const providerId = gap.providerId?.trim().toLowerCase();
  const field = gap.field?.trim().toLowerCase();
  // 1. Exact provider/domain match (the strongest signal).
  if (providerId) {
    const byDomain = vars.find((v) => v.domain.toLowerCase() === providerId);
    if (byDomain) return pickVar(byDomain);
  }
  // 2. Label / path contains the gap field (substring, both directions).
  if (field && field.length >= 3) {
    const byLabel = vars.find((v) => {
      const label = v.label.toLowerCase();
      const path = v.path.toLowerCase();
      return label.includes(field) || field.includes(label) || path.includes(field);
    });
    if (byLabel) return pickVar(byLabel);
  }
  return null;
}

function pickVar(v: ReturnType<typeof extractSEDVariables>[number]) {
  return { path: v.path, label: v.label, domain: v.domain, value: v.value, unit: v.unit };
}

/** Keep explicit Unknown property-absent in a rerun draft; never materialize it as Mixed. */
function withResolvedRerunWorkload(intent: ProjectIntent): ProjectIntent {
  const workloadProfile = effectiveWorkloadProfile(intent);
  return workloadProfile === 'unknown'
    ? { ...intent, workloadProfile: undefined }
    : { ...intent, workloadProfile };
}

export function FeasibilityRerunPanel({ report, open, onClose, gapCategorizer }: RerunPanelProps) {
  const rerunNavigate = useNavigate();
  const { generateTypedReport, siteRag, notifyAdmins } = useStore();
  const refreshSedSections = useStore(s => s.refreshSedSections);
  const saveSEDAmendment = useStore(s => s.saveSEDAmendment);
  // RP2 — in-place resolve dialog for a data gap (construct a SEDAmendment AT
  // THE VALUE SOURCE instead of the old "note the gap" no-op).
  const [resolveDraft, setResolveDraft] = React.useState<ResolveAmendmentDraft | null>(null);
  const [resolveGapIdx, setResolveGapIdx] = React.useState<number | null>(null);
  // Lazy-load the SED when the user enters the review step so the
  // PreflightFreshnessCard can render. Loading on panel-open would
  // pull GCS work into a path the user might never reach.
  const [reviewSed, setReviewSed] = React.useState<SiteEvidenceDocument | null>(null);
  const [reviewSedLoading, setReviewSedLoading] = React.useState(false);
  const [isPreflightRefreshing, setIsPreflightRefreshing] = React.useState(false);
  const [sectionColumnLayouts, setSectionColumnLayouts] = React.useState<Record<string, 1 | 2 | 3>>(() => report.sectionColumnLayouts ?? {});
  // Reader Polish Prompt 8 — review-step refresh decision lifted up
  // from the freshness card. Default false (refresh on rerun); the
  // "Skip refresh →" link in the inline list flips it.
  const [skipRefresh, setSkipRefresh] = React.useState(false);
  // Public Sentiment only: re-read the external sources instead of reusing the
  // site evidence's recent scan (the default). Confirmed when ticked.
  const [refreshSentimentScan, setRefreshSentimentScan] = React.useState(false);
  // Auto-skip step 1 when there's nothing to act on (Reader Polish
  // follow-up). The provider gap list is derived synchronously from
  // `report.sedGaps` + the conclusion-side gap list — so we can pick
  // the right starting step on first mount. The user can still click
  // the "Provider data status" tab to revisit it.
  const initialGapsExist = (() => {
    const sedGaps = report.sedGaps?.length ?? 0;
    const conclusionGaps = report.conclusion?.topConstraints?.length ?? 0;
    return sedGaps > 0 || conclusionGaps > 0;
  })();
  const [step, setStep] = useState<'gaps' | 'config' | 'prefs' | 'review' | 'running' | 'diff'>(
    initialGapsExist ? 'gaps' : 'config',
  );

  // Reader Polish follow-up — URL state for the rerun modal. Each
  // step is reflected in `?rerun=vN->vM&step=...` so a deep-link
  // opens the modal at the right step and back-button works.
  // The mirror is one-way (state → URL); the open / close decision
  // stays parent-owned via the `open` prop.
  const versionLabel = `v${report.version || 1}->v${(report.version || 1) + 1}`;
  useUrlSearchParamSync('rerun', open ? versionLabel : null);

  // Esc key closes the modal. Only mounted while open so non-rerun
  // surfaces don't have to share the listener.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Per-type preferences (Workflow Prompt 2). Seeded from the parent
  // report's typedPreferences[reportType] when present, otherwise
  // derived from the projectIntent the parent was generated under.
  // The Rerun panel mounts the same PreferencesFormFor the wizard
  // uses so a Power-availability rerun can edit btmStrategy,
  // Environmental can edit expectedNepaPathway, etc. — replacing the
  // legacy 7-line wrappers that only edited projectIntent.
  const parentReportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  // Re-runs on every mount of the panel (useState seeds from the memo,
  // and the memo re-evaluates when `report` changes / on a fresh mount)
  // — so a legacy report that only carries a ProjectIntent always opens
  // Step 3 with the typed fields back-filled from that intent.
  // Rerun Wizard Coherence W5.
  const initialEditedPrefs = useMemo<TypedPreferencesByType[ReportTypeId]>(
    () => deriveInitialRerunPrefs(report, parentReportType),
    [report, parentReportType],
  );
  // SCR-4 typed-prefs resolver is declared further down (right after
  // `owningSite`) so its memo can read `owningSite?.typedPreferencesDefaults`.
  // The `editedPrefs` useState moves with it so first-render state can
  // see the live-site overlay.

  // FF1 / FF2 — single source of truth for the site that owns this
  // report. The freshness check, the loader, the divergence chip, and
  // the refresh callbacks all read from this same memo so a SED
  // rebuild on the site (sedId bumps) is reflected consistently in
  // every surface. Falls back to a name-keyed lookup for legacy
  // reports that never recorded `siteId` (pre-site-bootstrap).
  //
  // SC6 also reads `owningSite.siteContext` from this memo for the
  // rerun-step site-context seed (see below). Single owningSite for
  // both packs — keep the FF version since it's strictly more general
  // (handles pre-site-bootstrap legacy reports the SC version drops).
  const owningSite = useMemo(() => {
    return siteForReport(siteRag.sites, report);
  }, [report.id, report.siteId, report.siteName, siteRag.sites]);

  // FF1 — telemetry / chip predicate: true when the site has been
  // rebuilt (or otherwise re-bootstrapped) since this report was
  // generated, so the site's current SED id no longer matches the
  // one this report pinned at render time.
  const sedIdDiverged = !!(
    owningSite?.sedId && report.sedId && owningSite.sedId !== report.sedId
  );

  // SCR-4 (row 0.99291) — typed-prefs surface honours the freshest-wins
  // rule too. When the live site's `typedPreferencesDefaults[reportType]`
  // is newer than the report's `createdAt`, we seed `editedPrefs` from the
  // live site (merged onto `initialEditedPrefs` so intent-derived
  // back-fill is preserved as a baseline). Otherwise we fall through to
  // the existing snapshot-derived `initialEditedPrefs`.
  // `initialEditedPrefs` itself stays unchanged so the Step-4 review diff
  // + auto-version-note keep reading the canonical snapshot-derived
  // baseline — only the user-editable `editedPrefs` state is bumped to
  // the live-site value on the live-site path.
  const scrTypedPrefs = useMemo(() => {
    const livePrefs = owningSite?.typedPreferencesDefaults?.[parentReportType] as
      | TypedPreferencesByType[ReportTypeId]
      | undefined;
    const snapshotPrefs = report.rerunConfig?.typedPreferences?.[parentReportType] as
      | TypedPreferencesByType[ReportTypeId]
      | undefined;
    return resolveFreshestSiteConfig<TypedPreferencesByType[ReportTypeId]>({
      liveValue: livePrefs,
      snapshotValue: snapshotPrefs,
      defaultValue: initialEditedPrefs,
      liveUpdatedAt: owningSite?.siteConfigUpdatedAt,
      reportGeneratedAt: report.createdAt,
    });
  }, [
    owningSite?.typedPreferencesDefaults,
    owningSite?.siteConfigUpdatedAt,
    report.rerunConfig?.typedPreferences,
    report.createdAt,
    parentReportType,
    initialEditedPrefs,
  ]);
  const scrTypedPrefsSource: ScrSource = scrTypedPrefs.source;
  const scrInitialEditedPrefs: TypedPreferencesByType[ReportTypeId] =
    scrTypedPrefsSource === 'live-site'
      ? ({ ...(initialEditedPrefs ?? {}), ...(scrTypedPrefs.value ?? {}) } as TypedPreferencesByType[ReportTypeId])
      : initialEditedPrefs;
  const [editedPrefs, setEditedPrefs] = useState<TypedPreferencesByType[ReportTypeId]>(scrInitialEditedPrefs);
  const prefsValidation = useMemo(
    () => validatePreferencesFor(parentReportType, editedPrefs),
    [parentReportType, editedPrefs],
  );

  // Site_Context SC6 — site-context edit on the rerun Configuration step.
  // Inherits the SITE's current value (the canonical ground truth) so an
  // 'edit-site' tweak picks up where the user left off. 'rerun-override'
  // mode lets the user inject a one-off fact for THIS report only without
  // mutating the site. The mode default is 'edit-site' — the common case.
  // RWX-2 — the inner Site Context mode toggle is hidden post-RWX-2
  // (SSOT directive), so `setSiteContextMode` is now unused. The state
  // remains because handleRerun + the audit-log breadcrumb still read
  // `siteContextMode` and we keep the field union compatible with the
  // SiteContextInput's typed prop (which is consumed without the
  // toggle render in this surface).
  // SC2-8 — prefer the canonical SC2 `siteContextAtGen` field; fall back
  // to the legacy `siteContextSnapshot` alias for reports generated
  // before SC2-2 shipped, then to the live site doc for the rare case
  // where neither was stamped (legacy reports with a deleted snapshot).
  // SCR-4 (row 0.99291) — overlay the freshest-wins resolver on the
  // siteContextText init so an edit to `site.siteContext.text` on the
  // Site Overview tab AFTER this report was generated picks up here too.
  // The snapshot path (`siteContextAtGen` / `siteContextSnapshot`)
  // preserves reproducibility for older reports. `seedSiteContextTextLegacy`
  // is the pre-SCR fallback chain — kept as the resolver's `defaultValue`
  // so legacy reports without snapshot AND without a fresher live edit
  // get exactly their prior init.
  const seedSiteContextTextLegacy = (
    report.siteContextAtGen?.text
    ?? report.siteContextSnapshot?.text
    ?? owningSite?.siteContext?.text
    ?? ''
  );
  const scrSiteContext = resolveFreshestSiteConfig<string>({
    liveValue: owningSite?.siteContext?.text,
    snapshotValue: report.siteContextAtGen?.text ?? report.siteContextSnapshot?.text,
    defaultValue: seedSiteContextTextLegacy,
    liveUpdatedAt: owningSite?.siteConfigUpdatedAt,
    reportGeneratedAt: report.createdAt,
  });
  const seedSiteContextText = scrSiteContext.value;
  const scrSiteContextSource: ScrSource = scrSiteContext.source;
  const [siteContextText, setSiteContextText] = useState<string>(seedSiteContextText);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [siteContextMode, _setSiteContextMode] = useState<SiteContextEditMode>('edit-site');

  /**
   * Site_Configuration_Consolidation SC2-6 — three-way rerun config mode.
   *
   * RWX-2 (2026-05-17) — per SSOT (row 0.99187), Site Project Intent
   * + typed-pref defaults are the canonical home for the shared fields.
   * The 3-way radio that asked "use site config as-is / edit and apply
   * to site / override for this rerun only" is gone (W4 from the live
   * walkthrough): two of the three options were the same per-report-
   * override mechanism SSOT explicitly retires. The default is now
   * `'apply-to-site'` — every edit the user makes here persists to the
   * site canonical so future reports inherit it. The `'override-only'`
   * value is kept in the type union so historical reports / test
   * fixtures that constructed the state in that mode don't blow up at
   * runtime, but the picker is gone. The remaining handleRerun branch
   * for `'override-only'` is dead code on the live UI today; we keep
   * it for back-compat (e.g. dev-tools / regression rigs that may
   * dispatch the value directly) and let CI sentinel guard against
   * reintroducing the radio.
   *
   * The `siteContextMode` ('edit-site' vs 'rerun-override') from the
   * SC pack is similarly collapsed — the inner radio on the Site
   * Context input is gone; the value stays pinned to `'edit-site'` so
   * every Site Context edit also lands on the canonical site doc.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rerunConfigMode, _setRerunConfigMode] = useState<'as-is' | 'apply-to-site' | 'override-only'>('apply-to-site');
  const [isApplyConfirmOpen, setIsApplyConfirmOpen] = useState<boolean>(false);
  // Live snapshot of claims + conflicts, populated by the extractor. Starts
  // from the site's current SiteContext so the wizard mounts with the
  // already-parsed claims if the site has them.
  const [siteContext, setSiteContext] = useState<SiteContext | undefined>(owningSite?.siteContext);
  const [siteContextExtractionStatus, setSiteContextExtractionStatus] = useState<'idle' | 'running' | 'error'>('idle');

  /**
   * Re-run extractor + cross-checker for a given snippet without persisting
   * to the site. Used by both edit-modes so the conflict list updates as
   * the user types. 'edit-site' mode also persists via updateSiteContext
   * on the rerun submit; 'rerun-override' mode just threads the local
   * snapshot through to generateTypedReport.
   */
  const recomputeSiteContextLocal = React.useCallback(async (text: string) => {
    if (!text || text.trim().length === 0) {
      setSiteContext({
        ...emptySiteContext(),
        text,
      });
      setSiteContextExtractionStatus('idle');
      return;
    }
    setSiteContextExtractionStatus('running');
    try {
      // EX-4 — extractClaimsWithRetry (the SX-1 auto-re-extract
      // orchestrator) instead of the raw extractClaims; identical
      // contract, re-extracts once when the first pass produced
      // schema-rejected rows.
      const { extractClaimsWithRetry } = await import('../../../services/siteContext/extractClaims');
      const { crossCheckClaims } = await import('../../../services/siteContext/crossCheckClaims');
      const { inferSiteStateFromSed } = await import('../../../lib/inferSiteState');
      // CLP-1 — capture the full result so the local snapshot carries
      // status + error fields too. Pre-CLP the rerun panel discarded
      // status the way the store action did, so a 429 here also went
      // silent. The extractor never throws now (returns a classified
      // result on every path), so the outer try/catch is belt-and-
      // braces — kept for defense against dynamic-import failures.
      // EX-5 — thread the already-loaded reviewSed + its inferred state
      // code so the extractor's cross-field rules fire.
      const res = await extractClaimsWithRetry(text, {
        siteName: report.siteName,
        reportType: parentReportType,
        sed: reviewSed,
        siteStateCode: reviewSed ? inferSiteStateFromSed(reviewSed) : null,
      });
      const conflicts = crossCheckClaims(res.claims, reviewSed);
      const liveIds = new Set(conflicts.map(c => c.id));
      const acks = (siteContext?.acknowledgedConflictIds ?? []).filter((id) => liveIds.has(id));
      setSiteContext({
        text,
        updatedAt: new Date().toISOString(),
        claims: res.claims,
        conflicts,
        acknowledgedConflictIds: acks,
        extractorVersion: res.extractorVersion,
        extractorStatus: res.status,
        extractorError: res.errorMessage,
        extractedAt: new Date(res.attemptedAt).toISOString(),
      });
      setSiteContextExtractionStatus(res.status === 'success' ? 'idle' : 'error');
    } catch (e) {
      const msg = (e as Error)?.message ?? String(e);
      console.warn('[site-context rerun] extraction failed:', msg);
      setSiteContext({
        text,
        updatedAt: new Date().toISOString(),
        claims: [],
        conflicts: [],
        acknowledgedConflictIds: [],
        extractorVersion: 'extractor-error',
        extractorStatus: 'unknown-error',
        extractorError: msg,
        extractedAt: new Date().toISOString(),
      });
      setSiteContextExtractionStatus('error');
    }
  }, [report.siteName, parentReportType, reviewSed, siteContext?.acknowledgedConflictIds]);

  const handleAcknowledgeContextConflict = React.useCallback((conflictId: string) => {
    setSiteContext((prev) => {
      if (!prev) return prev;
      if (prev.acknowledgedConflictIds.includes(conflictId)) return prev;
      return { ...prev, acknowledgedConflictIds: [...prev.acknowledgedConflictIds, conflictId] };
    });
    // SC9 — audit-log the override so the admin Activity tab can track
    // how often HIGH conflicts get acknowledged vs. resolved.
    void emitAuditEvent({
      action: 'site.context_conflict_override',
      target: { type: 'site', id: report.siteId ?? 'unknown', label: report.siteName },
      metadata: { conflictId, mode: siteContextMode },
    });
  }, [report.siteId, report.siteName, siteContextMode]);

  /** True when the panel has at least one HIGH-severity unacknowledged
   *  conflict — blocks submit until the user acknowledges or edits. */
  const siteContextHasBlockingConflicts = useMemo(() => {
    const ctx = siteContext;
    if (!ctx) return false;
    const acked = new Set(ctx.acknowledgedConflictIds);
    return ctx.conflicts.some(c => c.severity === 'high' && !acked.has(c.id));
  }, [siteContext]);

  // Load the SED on transition to review so the freshness card can
  // render. We don't reload if the user toggles back/forward through
  // the steps unless the site's SED changes.
  //
  // FF1 — Rerun Freshness Site-SED Fix:
  // The freshness check measures what the NEXT rerun will read, not
  // what the PRIOR report did read. The site owns the canonical SED
  // (per SA pack — reports project from the site's SED at render
  // time). If the site has been rebuilt since this report was
  // generated, `report.sedId` is an orphaned pointer — loading it
  // would return null, and `computeSedSectionStatuses(null)` would
  // flag every section as "never collected" even when the site's
  // current SED is fully populated. So: prefer `owningSite.sedId +
  // sedStoragePath`; fall back to `report.sedId` only when the site
  // has no SED at all (legacy reports pre-site-bootstrap).
  const targetSedId = owningSite?.sedId ?? report.sedId;
  const targetSedPath = owningSite?.sedId ? owningSite.sedStoragePath : undefined;
  React.useEffect(() => {
    // RDF-2 (hoist) — load the SED on panel-open (rather than only when
    // the user advances to step==='review') so the landing-card delta
    // can diff pinned `report.sedGaps` against the live SED's gaps for
    // a real `resolvedGaps` signal. Loading is still gated on
    // `open && targetSedId` to avoid GCS work for closed panels.
    if (!open) return;
    if (!targetSedId) { setReviewSed(null); return; }
    let cancelled = false;
    setReviewSedLoading(true);
    (async () => {
      try {
        const { loadSedById } = await import('../../../services/sedLoader');
        const doc = await loadSedById(targetSedId, targetSedPath);
        if (!cancelled) setReviewSed(doc);
      } catch {
        if (!cancelled) setReviewSed(null);
      } finally {
        if (!cancelled) setReviewSedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetSedId, targetSedPath]);

  // FF2 — single helper used by every refresh callback so we always
  // resolve the site by id when possible (and fall back to name only
  // when `report.siteId` was never set). Previously each callback
  // re-did the name-keyed lookup inline; centralising it means a
  // future site-id rewrite touches one place.
  const resolveOwningSiteId = (): string | undefined =>
    owningSite?.id ?? report.siteId ?? undefined;

  const handleRerunWithRefresh = async (sectionIds: SedSectionId[]) => {
    // The report worker owns Public Sentiment collection. A browser top-up
    // would scan twice and publish evidence before this report is queued.
    if ((report.reportType ?? 'feasibility') === 'public-sentiment') sectionIds = sectionIds.filter(id => id !== 'publicSentiment');
    if (sectionIds.length === 0) { void handleRerunRequest(); return; }
    const resolvedSiteId = resolveOwningSiteId();
    if (!resolvedSiteId) { void handleRerunRequest(); return; }
    setIsPreflightRefreshing(true);
    try {
      // FF2 — sectionIds come from `requiredStale` which is derived
      // from `reviewSed` (the site's CURRENT SED post-FF1), so the
      // ids are guaranteed to match the schema `refreshSedSections`
      // will run against. The store action keys by siteId, not sedId,
      // so divergence between `owningSite.sedId` and `report.sedId`
      // is invisible downstream.
      await refreshSedSections(resolvedSiteId, sectionIds);
    } catch (e) {
      console.warn('[rerun-preflight-refresh] failed:', (e as Error)?.message);
      toast.error("Couldn't refresh evidence — generate anyway?", {
        action: { label: 'Generate anyway', onClick: () => void handleRerunRequest() },
        cancel: { label: 'Cancel', onClick: () => undefined },
        duration: 12_000,
      });
      setIsPreflightRefreshing(false);
      return;
    }
    setIsPreflightRefreshing(false);
    void handleRerunRequest();
  };

  const handleRerunSkipRefresh = (staleSectionIds: SedSectionId[]) => {
    void emitAuditEvent({
      action: 'preflight-refresh-skipped',
      target: { type: 'site', id: resolveOwningSiteId() ?? report.id, label: report.siteName },
      metadata: {
        reportType: report.reportType ?? 'feasibility',
        staleSectionIds,
        surface: 'rerun-panel',
        // FF2 — telemetry: capture when the freshness panel that drove
        // the skip decision was measured against a site SED that had
        // diverged from the report's pinned pointer.
        sedIdDiverged,
        siteSedId: owningSite?.sedId,
        reportSedId: report.sedId,
      },
    });
    void handleRerunRequest();
  };

  const handleRerunRefreshOptional = async (sectionIds: SedSectionId[]) => {
    if ((report.reportType ?? 'feasibility') === 'public-sentiment') sectionIds = sectionIds.filter(id => id !== 'publicSentiment');
    if (sectionIds.length === 0) return;
    const resolvedSiteId = resolveOwningSiteId();
    if (!resolvedSiteId) return;
    setIsPreflightRefreshing(true);
    try { await refreshSedSections(resolvedSiteId, sectionIds); }
    catch (e) {
      console.warn('[rerun-preflight-refresh-optional] failed:', (e as Error)?.message);
      toast.error('Optional refresh failed — continuing with current evidence.');
    }
    finally { setIsPreflightRefreshing(false); }
  };
  const [newReportForDiff, setNewReportForDiff] = useState<SiteRagReport | null>(null);
  // Capture the original report before rerun (props change when activeReportId updates)
  const [originalReport, setOriginalReport] = useState<SiteRagReport>(report);
  const [isRunning, setIsRunning] = useState(false);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  // Async generation returns after the job is queued, not after a report is
  // committed. Track that distinction explicitly so queued work cannot fall
  // through the synchronous "find the new report" path.
  const [asyncQueuedRunId, setAsyncQueuedRunId] = useState<string | null>(null);
  const [asyncHydrationAttempt, setAsyncHydrationAttempt] = useState(0);
  const [asyncCompletionError, setAsyncCompletionError] = useState<string | null>(null);
  const rerunSubmitInFlightRef = React.useRef(false);
  const handledAsyncOutcomeRef = React.useRef<string | null>(null);
  const activeGenerations = useStore(s => s.siteRag.activeGenerations ?? STABLE_EMPTY_RECORD);
  const generationOutcomes = useStore(s => s.siteRag.generationOutcomes ?? STABLE_EMPTY_RECORD);
  const currentRun = currentRunId ? activeGenerations[currentRunId] : undefined;
  // The server beat lives in its own slice, not on the run (see generationHeartbeats).
  const currentRunHeartbeatAt = useGenerationHeartbeat(currentRunId);
  const currentOutcome = currentRunId ? generationOutcomes[currentRunId] : undefined;
  // Public Sentiment reruns land here (RerunWrapper's default branch). A rerun
  // the store HELD for site evidence (needs a rebuild, or its automatic
  // retries ran out) was never dispatched: explained inline, never "failed".
  const sentimentEvidenceHold = useSentimentEvidenceHold(
    (report.reportType ?? 'feasibility') === 'public-sentiment' ? (report.siteId ?? undefined) : undefined,
  );
  const scopedProgress = currentRun?.progress
    ?? (currentOutcome?.status === 'succeeded'
      ? 100
      : currentRunId
        ? 0
        : siteRag.progress);
  const scopedProgressStage = currentRun?.progressStage
    ?? (currentOutcome?.status === 'succeeded'
      ? 'Finalizing generated report…'
      // A freshly submitted run exists locally before its Firestore job
      // snapshot arrives. Falling through to the store-wide progress at that
      // moment can label a Water/Public Sentiment rerun as the unrelated Power
      // job already running on the same site. Keep the placeholder scoped to
      // this run until its own authoritative stage hydrates.
      : currentRunId
        ? 'Initializing…'
        : siteRag.progressStage);
  const scopedError = asyncCompletionError
    ?? (currentOutcome?.status === 'failed'
      ? (currentOutcome.error ?? 'Report generation failed.')
      : currentOutcome?.status === 'cancelled'
        ? (currentOutcome.error ?? 'Report generation was cancelled.')
        : currentRunId
          ? null
          : siteRag.error);

  const owningSiteId = report.siteId ?? siteForReport(siteRag.sites, report)?.id;

  useGenerationBeforeUnloadGuard({
    enabled: open && Boolean(owningSiteId),
    warnOnNavigate: step === 'running',
    siteId: owningSiteId ?? '',
    reportType: report.reportType ?? 'feasibility',
    step: 'generating',
    runId: currentRunId,
  });

  React.useEffect(() => {
    if (!open || step === 'running') return;
    const resolvedSiteId = report.siteId ?? siteForReport(siteRag.sites, report)?.id;
    if (!resolvedSiteId) return;
    // TYPE-scoped reattach (2026-08-13, Redhead): this panel used to attach
    // to ANY live run on the site — opening the PSR rerun while a
    // feasibility run was in flight showed "GENERATING BTM FEASIBILITY"
    // under a Public Sentiment title AND blocked starting the PSR rerun.
    // A panel only ever resumes ITS OWN report type; other types' runs are
    // visible on the landing-page cards.
    const panelType = report.reportType ?? 'feasibility';
    const runs = Object.values(activeGenerations).filter(
      r => r.siteId === resolvedSiteId && r.reportTypeId === panelType,
    );
    if (runs.length === 0) return;
    const latest = runs.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0];
    if (!latest) return;
    setCurrentRunId(latest.id);
    setStep('running');
    setIsRunning(true);
    if (latest.jobId) {
      rerunSubmitInFlightRef.current = true;
      handledAsyncOutcomeRef.current = null;
      setAsyncCompletionError(null);
      setAsyncHydrationAttempt(0);
      setAsyncQueuedRunId(latest.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeGenerations, report.siteId, report.siteName, report.reportType]);

  // Async completion bridge. The Firestore job is authoritative for terminal
  // status and for the exact report id. A succeeded job can arrive one render
  // before fetchRagReports hydrates that report, so retry the report read for a
  // bounded window. Never infer completion from report ordering or timestamps.
  React.useEffect(() => {
    if (!open || !asyncQueuedRunId) return;
    const outcome = generationOutcomes[asyncQueuedRunId];
    if (!outcome) return;

    const outcomeKey = [
      asyncQueuedRunId,
      outcome.status,
      outcome.reportId ?? '',
      outcome.error ?? '',
    ].join(':');

    if (outcome.status === 'failed' || outcome.status === 'cancelled') {
      if (handledAsyncOutcomeRef.current === outcomeKey) return;
      handledAsyncOutcomeRef.current = outcomeKey;
      rerunSubmitInFlightRef.current = false;
      setIsRunning(false);
      const message = outcome.error
        ?? (outcome.status === 'cancelled'
          ? 'Report generation was cancelled.'
          : 'Report generation failed.');
      setAsyncCompletionError(message);
      toast.error(`Rerun failed: ${message}`);
      return;
    }

    if (outcome.status !== 'succeeded') return;
    if (!outcome.reportId) {
      if (handledAsyncOutcomeRef.current === outcomeKey) return;
      handledAsyncOutcomeRef.current = outcomeKey;
      rerunSubmitInFlightRef.current = false;
      setIsRunning(false);
      const message = 'Report generation succeeded without returning a report id.';
      setAsyncCompletionError(message);
      toast.error(`Rerun failed: ${message}`);
      return;
    }

    const generatedReport = siteRag.reports.find(candidate => candidate.id === outcome.reportId);
    if (generatedReport) {
      const expectedSiteId = outcome.siteId ?? report.siteId;
      const expectedReportType = report.reportType ?? 'feasibility';
      if (
        (expectedSiteId && generatedReport.siteId !== expectedSiteId)
        || generatedReport.reportType !== expectedReportType
      ) {
        if (handledAsyncOutcomeRef.current === outcomeKey) return;
        handledAsyncOutcomeRef.current = outcomeKey;
        rerunSubmitInFlightRef.current = false;
        setIsRunning(false);
        const message = 'The completed job returned a report outside the requested site or report type.';
        setAsyncCompletionError(message);
        toast.error(`Rerun failed: ${message}`);
        return;
      }
      if (handledAsyncOutcomeRef.current === outcomeKey) return;
      handledAsyncOutcomeRef.current = outcomeKey;
      rerunSubmitInFlightRef.current = false;
      setIsRunning(false);
      setAsyncCompletionError(null);
      toast.success(`Report v${generatedReport.version || (report.version || 1) + 1} generated`);
      useStore.getState().setActiveRagReport(null);
      const targetSiteId = expectedSiteId ?? generatedReport.siteId;
      rerunNavigate(`/sites/${targetSiteId}?tab=reports&rerunReportId=${generatedReport.id}`);
      return;
    }

    if (asyncHydrationAttempt >= ASYNC_REPORT_HYDRATION_MAX_ATTEMPTS) {
      if (handledAsyncOutcomeRef.current === outcomeKey) return;
      handledAsyncOutcomeRef.current = outcomeKey;
      rerunSubmitInFlightRef.current = false;
      setIsRunning(false);
      const message = `Generated report ${outcome.reportId} did not hydrate after the job completed.`;
      setAsyncCompletionError(message);
      toast.error(`Rerun failed: ${message}`);
      return;
    }

    const timer = window.setTimeout(() => {
      void useStore.getState().fetchRagReports()
        .catch((error: unknown) => {
          console.warn('[rerun] completed report hydration failed:', (error as Error)?.message);
        })
        .finally(() => setAsyncHydrationAttempt(attempt => attempt + 1));
    }, ASYNC_REPORT_HYDRATION_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [
    open,
    asyncQueuedRunId,
    asyncHydrationAttempt,
    generationOutcomes,
    siteRag.reports,
    report.siteId,
    report.reportType,
    report.version,
    rerunNavigate,
  ]);
  // Rerun Reimagined RR6 — version note pre-filled from the generated
  // string. Tracks whether the user has hand-edited it so subsequent
  // delta changes don't clobber an explicit user edit.
  const [versionNote, setVersionNote] = useState('');
  const [versionNoteUserEdited, setVersionNoteUserEdited] = useState(false);
  const setVersionNoteFromUser = React.useCallback<React.Dispatch<React.SetStateAction<string>>>(
    (next) => {
      setVersionNote(next);
      setVersionNoteUserEdited(true);
    },
    [],
  );
  const [keyDialog, setKeyDialog] = useState<{ provider: ApiProviderDef; gapIdx: number } | null>(null);

  // Parse data gaps from report — include sedGaps, conclusion gaps, AND unavailable sources.
  // Scoped to the report's reportType: dataset-completeness checks only fire
  // for sections the report type actually requires, so non-feasibility
  // reruns (Environmental, Water, etc.) don't surface feasibility-shaped
  // noise like "missing solar data" on a water-resource report.
  const [gaps, setGaps] = useState<DataGapItem[]>(() => {
    const items: DataGapItem[] = [];
    const reportTypeId = report.reportType ?? 'feasibility';
    const requiredSections = new Set(REPORT_TYPE_REGISTRY[reportTypeId]?.requiredSedSections ?? []);
    for (const gap of report.sedGaps || []) {
      const match = gap.match(/^\[(\w+)\]\s*(.+?):\s*(.+)$/);
      if (match) {
        // Content-Source Fixes Prompt 10 — pluck the trailing
        // `[provider:<id>]` token (if present) off the description
        // so classifyGap can route via exact-match. The user-visible
        // text drops the token.
        const rawDesc = match[3];
        const providerMatch = rawDesc.match(/\s*\[provider:([\w-]+)\]\s*$/);
        const description = providerMatch ? rawDesc.replace(/\s*\[provider:[\w-]+\]\s*$/, '').trim() : rawDesc;
        const providerId = providerMatch ? providerMatch[1] : undefined;
        items.push({ field: match[2], description, impact: match[1] as any, resolved: false, ignored: false, ...(providerId ? { providerId } : {}) });
      } else {
        items.push({ field: 'unknown', description: gap, impact: 'significant', resolved: false, ignored: false });
      }
    }
    // Add gaps from criticalDataGaps in conclusion
    for (const cond of report.conclusion?.keyConditions || []) {
      if (cond.startsWith('DATA GAP:')) {
        const desc = cond.replace('DATA GAP: ', '').replace('. Update via Evidence Data and regenerate.', '');
        if (!items.some(i => i.description.includes(desc.slice(0, 30)))) {
          items.push({ field: 'data', description: desc, impact: 'critical', resolved: false, ignored: false });
        }
      }
    }
    // Identify specific unavailable data sources from references and missing datasets
    const unavailableRefs = (report.references || []).filter(r => r.source.includes('UNAVAILABLE'));
    for (const ref of unavailableRefs) {
      const desc = ref.source.replace(/UNAVAILABLE/g, 'unavailable').replace(/—/g, '—').trim();
      if (!items.some(i => i.field === ref.field)) {
        items.push({ field: ref.field, description: desc, impact: 'significant', resolved: false, ignored: false });
      }
    }
    // Check for missing optional datasets — gated on the type's required
    // sections so e.g. an Environmental rerun doesn't show "missing solar".
    const datasetChecks: { key: string; label: string; desc: string }[] = [
      { key: 'seismic', label: 'Seismic Data', desc: 'USGS seismic hazard data was not retrieved. Rerunning may resolve transient API failures.' },
      { key: 'solar', label: 'Solar Resource', desc: 'NREL solar irradiance data was not retrieved.' },
      { key: 'wind', label: 'Wind Resource', desc: 'NREL wind speed data was not retrieved.' },
      { key: 'environmental', label: 'Environmental', desc: 'EPA environmental screening data was not retrieved.' },
      { key: 'electricity', label: 'Electricity Rates', desc: 'EIA electricity rate data was not retrieved.' },
      { key: 'airQuality', label: 'Air Quality', desc: 'AirNow air quality index data was not retrieved.' },
      { key: 'connectivity', label: 'Network Connectivity', desc: 'PeeringDB internet exchange proximity data was not retrieved.' },
      { key: 'parcel', label: 'Parcel Data', desc: 'Regrid parcel/zoning data was not retrieved.' },
    ];
    const ds = report.datasets;
    if (ds) {
      for (const check of datasetChecks) {
        // Skip checks that aren't relevant to this report type. When the
        // type's requiredSedSections is empty (legacy / unknown) we fall
        // back to the original full set so existing behaviour is preserved.
        const inRequired = requiredSections.size === 0 || requiredSections.has(check.key);
        if (!inRequired) continue;
        if (!(ds as any)[check.key] && !items.some(i => i.field === check.key)) {
          items.push({ field: check.key, description: `${check.label}: ${check.desc}`, impact: 'minor', resolved: false, ignored: false });
        }
      }
    }
    // Add SED anomalies
    for (const anomaly of report.sedAnomalies || []) {
      if (!items.some(i => i.description.includes(anomaly.slice(0, 30)))) {
        items.push({ field: 'cross-validation', description: anomaly, impact: 'minor', resolved: false, ignored: false });
      }
    }
    return items;
  });

  // Rerun config from saved report. Legacy reports may carry only
  // `projectType`; effectiveWorkloadProfile resolves the migrated value so
  // the rerun editor opens on the right workload immediately.
  const config = report.rerunConfig || {};

  // SCR-1 (row 0.99291 — customer-trust data-loss hotfix) — freshest-wins
  // resolution of the wizard's `projectIntent` init. When the live site has
  // been edited AFTER the source report was generated (`siteConfigUpdatedAt
  // > report.createdAt`), we pull the LIVE value; otherwise we honour the
  // report's `rerunConfig.projectIntent` snapshot for reproducibility.
  // Hardcoded defaults remain the legacy fallback for blank/legacy reports.
  //
  // `owningSite` is the live SiteEntity resolved by the FF1/FF2 memo above.
  // The `siteConfigUpdatedAt` timestamp is stamped on every write by
  // `updateSiteProjectIntent` / `updateSiteTypedPreferencesDefault` /
  // `updateSiteContext` in `store/index.ts` (lines 4523, 4562, 4604).
  //
  // The `defaults` literal here preserves the pre-SCR hardcoded fallback
  // verbatim so legacy reports (no snapshot, no live config) keep their
  // exact prior initial state.
  const SCR_PROJECT_INTENT_DEFAULTS: ProjectIntent = {
    workloadProfile: 'ai-training',
    powerRequirement: '50-200mw',
    coolingStrategy: 'undecided',
    nuclearInterest: 'considering',
    timeline: '2-5yr',
    priority: 'resilience',
    customWeights: config.customWeights as ProjectIntent['customWeights'],
  };
  const scrProjectIntent = useMemo(
    () =>
      resolveFreshestSiteConfig<ProjectIntent>({
        liveValue: owningSite?.projectIntent,
        snapshotValue: config.projectIntent,
        defaultValue: SCR_PROJECT_INTENT_DEFAULTS,
        liveUpdatedAt: owningSite?.siteConfigUpdatedAt,
        reportGeneratedAt: report.createdAt,
        // wf wq07loep2 DATA-LOSS guard — a present, user-authored live intent
        // must never be discarded for the thin hardcoded default purely on
        // the timestamp comparison. `isNonEmpty` treats a rich intent as
        // present; `isRicher` (strict score `>`) lets a richer live intent
        // beat a thinner snapshot, while a tie keeps the snapshot's legacy
        // precedence so the reproducibility contract (SCR test 2) holds.
        isNonEmpty: (pi) => isRichProjectIntent(pi),
        isRicher: (live, candidate) =>
          projectIntentRichnessScore(live) > projectIntentRichnessScore(candidate),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      owningSite?.projectIntent,
      owningSite?.siteConfigUpdatedAt,
      config.projectIntent,
      report.createdAt,
    ],
  );
  const scrSource: ScrSource = scrProjectIntent.source;

  const [projectIntent, setProjectIntent] = useState<ProjectIntent>(() => {
    const seed: ProjectIntent = scrProjectIntent.value;
    return withResolvedRerunWorkload(seed);
  });

  // SCR-2 — dirty flag + tracked setter. Flips on any user-driven write to
  // `projectIntent` so a Zustand-driven update on `liveSite.projectIntent`
  // arriving while the modal is open cannot overwrite the user's in-progress
  // edits. The flag is cleared only by the SCR-3 "Use original" button.
  const userHasEditedIntentRef = React.useRef(false);
  const setProjectIntentTracked = React.useCallback<typeof setProjectIntent>(
    (updater) => {
      userHasEditedIntentRef.current = true;
      setProjectIntent(updater);
    },
    [],
  );

  // SCR-2 mid-flight resync. Only fires on the live-site branch (the
  // snapshot branch is by-definition frozen at gen time, so re-pulling
  // it on every render would defeat the snapshot guarantee). The dirty
  // ref pin in test 4 of FeasibilityRerunPanel.scr.test.tsx asserts a
  // user edit survives a concurrent live-site update.
  React.useEffect(() => {
    if (scrSource !== 'live-site') return;
    if (userHasEditedIntentRef.current) return;
    const livePI = owningSite?.projectIntent;
    if (!livePI) return;
    setProjectIntent((prev) => {
      // Cheap structural check before stomping local state. ProjectIntent
      // is ~10 fields / <2KB so JSON.stringify is well within budget (R5).
      if (JSON.stringify(prev) === JSON.stringify(livePI)) return prev;
      return withResolvedRerunWorkload(livePI);
    });
  }, [scrSource, owningSite?.projectIntent, owningSite?.siteConfigUpdatedAt]);

  // SCR-3 — restore-original handler used by the "Use original" button on
  // the WHAT CHANGED banner. Resets the dirty flag so SCR-2 can resume
  // tracking subsequent live-site updates, then writes the snapshot value
  // (when present) back into local state.
  const scrRestoreOriginal = React.useCallback(() => {
    userHasEditedIntentRef.current = false;
    const snapshot = config.projectIntent;
    if (snapshot) {
      setProjectIntent(withResolvedRerunWorkload(snapshot));
    }
  }, [config.projectIntent]);

  // SCR-6 — fire-once telemetry: which source the wizard's projectIntent
  // init pulled from. Soft-fail (the audit-log client already swallows
  // network errors), bounded to one event per modal open. Reading
  // `open` keeps the effect quiescent on closed mounts.
  React.useEffect(() => {
    if (!open) return;
    void emitAuditEvent({
      action: 'rerun.config.source',
      target: { type: 'report', id: report.id, label: report.siteName },
      metadata: {
        source: scrSource,
        siteConfigUpdatedAt: owningSite?.siteConfigUpdatedAt,
        reportCreatedAt: report.createdAt,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Document upload for gap resolution + additional data
  const [uploadedDocs, setUploadedDocs] = useState<import('../../../types/siteRag').SiteDocument[]>(() => {
    // Auto-fix documents with bad summaries from before server proxy fix
    return (report.documents || []).map(d => {
      if (d.summary === 'AI summarization unavailable.' || d.summary === 'AI summarization unavailable') {
        return { ...d, summary: d.fileName.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ') };
      }
      return d;
    });
  });
  // Sync ALL settings from library (priority, folder, includeInReport) + pick up new docs
  React.useEffect(() => {
    if (!report.documents) return;
    setUploadedDocs(prev => {
      const prevIds = new Set(prev.map(d => d.id));
      const reportDocMap = new Map((report.documents || []).map(d => [d.id, d]));
      // Update existing docs with fresh settings
      const updated = prev.map(d => {
        const lib = reportDocMap.get(d.id);
        return lib ? { ...d, priority: lib.priority, folder: lib.folder, includeInReport: lib.includeInReport } : d;
      });
      // Add new docs from library that weren't in the rerun list
      const newDocs = (report.documents || []).filter(d => !prevIds.has(d.id));
      return [...updated, ...newDocs];
    });
  }, [report.documents]);

  const [excludedDocIds, setExcludedDocIds] = useState<Set<string>>(new Set());
  const [isProcessingDoc, setIsProcessingDoc] = useState(false);
  const [docProgress, setDocProgress] = useState('');
  const [showDiffReview, setShowDiffReview] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Batch upload with progress — processes files one at a time to avoid crashes.
  // UFL-4 — the `valid` array has already been filtered by `filterValidFiles`
  // (which now emits honest per-reason toasts for rejected files). This guard
  // catches the "every file was rejected" case where `filterValidFiles` already
  // toasted; we just need to bail without the misleading folder-only message.
  const handleBatchUpload = async (valid: File[], _isFolder = false) => {
    if (valid.length === 0) { return; }
    setIsProcessingDoc(true);
    setDocProgress(`0/${valid.length}`);
    try {
      const { processUploadedFile, saveDocument } = await (await import('../../../utils/safeImport')).safeImport(() => import('../../../services/knowledge/documentUpload'));
      const newDocs: import('../../../types/siteRag').SiteDocument[] = [];
      for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        setDocProgress(`${i + 1}/${valid.length}`);
        try {
          const doc = await processUploadedFile(file);
          const rp = (file as any).webkitRelativePath || file.name;
          if (rp !== file.name) doc.fileName = rp;
          newDocs.push(doc);
          // Save to GCS individually (don't batch — avoids memory buildup).
          // A non-fatal raw-file failure surfaces as a structured warning so
          // the user knows preview/download of the original is unavailable.
          try {
            // #89 — pass the owning site id so the DOCUMENT write is
            // gated by that site's edit-lock (assertSiteWritable). A
            // locked-site upload by a non-holder throws here and is
            // surfaced via the best-effort catch.
            const saveResult = await saveDocument(doc, file, report.siteId);
            if (saveResult?.rawUploadWarning) {
              toast.warning(saveResult.rawUploadWarning.message);
            }
          } catch { /* metadata persistence best-effort */ }
        } catch (e) {
          console.error(`[Upload] Failed: ${file.name}`, e);
        }
      }
      // Add to rerun doc list
      setUploadedDocs(prev => [...prev, ...newDocs]);
      // Persist to report library so they're available for all future reruns
      try {
        const currentDocs = useStore.getState().siteRag.reports.find(r => r.id === report.id)?.documents || [];
        const existingIds = new Set(currentDocs.map(d => d.id));
        const trulyNew = newDocs.filter(d => !existingIds.has(d.id));
        if (trulyNew.length > 0) {
          useStore.getState().updateRagReport(report.id, { documents: [...currentDocs, ...trulyNew] });
        }
      } catch { /* best-effort */ }
      toast.success(`${newDocs.length} of ${valid.length} document(s) processed and saved to library`);
    } catch (e) {
      console.error('[Upload] Batch failed:', e);
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setIsProcessingDoc(false);
      setDocProgress('');
    }
  };

  // UFL-4 — shared rerun-wizard validator. Mirrors the DocumentLibrary
  // path: classify with the shared helper, surface honest per-reason
  // toasts for rejections, and return only the files that passed.
  const filterValidFiles = (files: FileList | null): File[] => {
    const { valid, rejected } = classifyUploadFileList(files);
    // UFL-5 — one audit event per rejection (best-effort soft-fail).
    for (const r of rejected) {
      emitUploadRejected({
        fileName: r.file.name,
        sizeBytes: r.file.size,
        reason: r.reason.kind,
        surface: 'feasibility-rerun',
        siteId: report.siteId,
        reportId: report.id,
        ext: r.reason.kind === 'unsupported-ext' ? r.reason.ext : undefined,
        capBytes: r.reason.kind === 'too-large' ? r.reason.capBytes : undefined,
      });
    }
    if (rejected.length === 0) return valid;
    if (valid.length === 0) {
      // Every file was rejected — one toast per distinct reason.
      for (const msg of summarizeRejections(rejected)) toast.error(msg);
    } else {
      // Partial — warn with details for up to the first 5 rejections.
      const head = rejected
        .slice(0, 5)
        .map(r => `• ${describeUploadRejection(r.file, r.reason)}`)
        .join('\n');
      const tail = rejected.length > 5 ? `\n…and ${rejected.length - 5} more.` : '';
      toast.warning(
        `Uploading ${valid.length} of ${valid.length + rejected.length} files. ${rejected.length} skipped:\n${head}${tail}`,
      );
    }
    return valid;
  };

  const handleUploadForGap = async (gapIdx: number, files: FileList | null) => {
    // filterValidFiles already toasted on rejection; bail if nothing
    // survived rather than emitting the legacy generic message.
    const valid = filterValidFiles(files);
    if (valid.length === 0) { return; }
    setIsProcessingDoc(true);
    try {
      const { processUploadedFile, saveDocument } = await (await import('../../../utils/safeImport')).safeImport(() => import('../../../services/knowledge/documentUpload'));
      const newDocs: import('../../../types/siteRag').SiteDocument[] = [];
      for (const file of valid) {
        try {
          const doc = await processUploadedFile(file);
          newDocs.push(doc);
          // #89 — pass the owning site id so the DOCUMENT write is gated
          // by that site's edit-lock (assertSiteWritable). A locked-site
          // upload by a non-holder throws and is caught below.
          const saveResult = await saveDocument(doc, file, report.siteId);
          if (saveResult?.rawUploadWarning) {
            toast.warning(saveResult.rawUploadWarning.message);
          }
        } catch (e) { console.error(`Failed to process ${file.name}:`, e); }
      }
      // Add to rerun document list
      setUploadedDocs(prev => [...prev, ...newDocs]);
      // Also save to report library for persistence
      try {
        const currentDocs = useStore.getState().siteRag.reports.find(r => r.id === report.id)?.documents || [];
        const existingIds = new Set(currentDocs.map(d => d.id));
        const trulyNew = newDocs.filter(d => !existingIds.has(d.id));
        if (trulyNew.length > 0) {
          useStore.getState().updateRagReport(report.id, { documents: [...currentDocs, ...trulyNew] });
        }
      } catch { /* best-effort library sync */ }
      if (newDocs.length > 0) {
        const existingRes = gaps[gapIdx]?.resolution || '';
        const newRes = newDocs.map(d => d.fileName).join(', ');
        setGaps(prev => prev.map((g, i) => i === gapIdx ? {
          ...g, resolved: true,
          resolution: existingRes ? `${existingRes}, ${newRes}` : `Document: ${newRes}`,
        } : g));
      }
      toast.success(`${newDocs.length} document(s) uploaded and saved to library`);
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      setIsProcessingDoc(false);
    }
  };

  const handleManualInput = (gapIdx: number) => {
    // RP2 — replace the old no-op "note the gap" toast with a real in-place
    // resolve. When the gap maps to a concrete SED variable, open the amendment
    // dialog AT THE VALUE SOURCE so the value is set BEFORE the rerun (routed
    // through the ONE persist path, saveSEDAmendment). When no SED slot can be
    // matched (no loaded SED, or the gap is a document-only gap), fall back to
    // the prior "add data via Evidence Data" note rather than fabricating a path.
    const gap = gaps[gapIdx];
    const match = gap && reviewSed ? matchSedVariableForGap(reviewSed, gap) : null;
    if (gap && reviewSed && match) {
      setResolveGapIdx(gapIdx);
      setResolveDraft({
        sedId: reviewSed.id,
        sedVersion: reviewSed.version,
        variablePath: match.path,
        variableLabel: match.label,
        domain: match.domain,
        oldValue: match.value,
        unit: match.unit,
        defaultReason: `Resolved data gap during rerun: ${gap.field}`,
        issueType: 'override',
      });
      return;
    }
    // Fallback — no resolvable SED slot; note the gap for post-generation entry.
    setGaps(prev => prev.map((g, i) => i === gapIdx ? { ...g, resolved: true, resolution: 'Will update via Evidence Data after generation' } : g));
    toast.info('Gap noted — add data via Evidence Data panel after report generates');
  };

  // Build auto version note from what changed
  const generateAutoVersionNote = (): string => {
    const parts: string[] = [];
    // Project-intent changes (workload / nuclear / priority — the fields
    // still editable on Step 2). The bucketed power/cooling/timeline knobs
    // moved to Step 3's typed prefs (covered below) — Rerun Wizard
    // Coherence W3/W6.
    const origIntent = report.rerunConfig?.projectIntent;
    if (origIntent) {
      const origWorkload = effectiveWorkloadProfile(origIntent);
      const newWorkload = effectiveWorkloadProfile(projectIntent);
      if (origWorkload !== newWorkload) parts.push(`workload → ${workloadProfileLabel(newWorkload)}`);
      if (origIntent.nuclearInterest !== projectIntent.nuclearInterest) parts.push(`nuclear → ${projectIntent.nuclearInterest}`);
      if (origIntent.priority !== projectIntent.priority) parts.push(`priority → ${projectIntent.priority}`);
    }
    // Typed-preference changes (Step 3 — the source of truth for project
    // size, cooling type, timing, and the per-type knobs). Diffed against
    // what the panel was seeded with on this mount.
    for (const d of typedPrefDeltas(
      initialEditedPrefs as Record<string, unknown>,
      editedPrefs as Record<string, unknown>,
    )) {
      parts.push(`${d.label} → ${d.to}`);
    }
    // New documents
    const origDocCount = (report.documents || []).length;
    const newDocCount = uploadedDocs.length - origDocCount;
    if (newDocCount > 0) parts.push(`+${newDocCount} document(s)`);
    // Gaps
    const resolved = gaps.filter(g => g.resolved).length;
    const ignored = gaps.filter(g => g.ignored).length;
    if (resolved > 0) parts.push(`${resolved} gap(s) addressed`);
    if (ignored > 0) parts.push(`${ignored} ignored`);
    // User comment
    if (versionNote.trim()) parts.push(versionNote.trim());

    return parts.length > 0
      ? `v${(report.version || 1) + 1}: ${parts.join(', ')}`
      : `Rerun from v${report.version || 1}`;
  };

  /**
   * SC2-6 — wrapper around handleRerun that surfaced an
   * "Apply these changes to the site?" confirm modal BEFORE the
   * write-through fired when `rerunConfigMode === 'apply-to-site'`.
   *
   * RWX-2 (2026-05-17) — with SSOT (row 0.99187) shipped and the 3-way
   * radio retired, edits ALWAYS land on the site canonical. The
   * confirm modal had purpose when "apply-to-site" was one of three
   * options the user might have picked deliberately; now that it's
   * the implicit default, surfacing the prompt on every rerun is
   * noise. The WHAT CHANGED card already lists what the rerun is
   * about to commit (resolvedGaps, configChanged, appliedFindings),
   * so the safeguard is moved up to the canvas itself. The modal
   * + `isApplyConfirmOpen` state stay defined below (dead UI) for
   * back-compat with the in-flight handleRerun audit-log path —
   * but the modal never renders because nothing flips the flag
   * anymore. CI sentinel guards reintroduction of the radio.
   */
  const handleRerunRequest = async () => {
    await handleRerun();
  };

  const handleApplyToSiteConfirm = async () => {
    setIsApplyConfirmOpen(false);
    await handleRerun();
  };

  const handleApplyToSiteCancel = () => {
    setIsApplyConfirmOpen(false);
  };

  const handleRerun = async () => {
    // The primary CTA and the Confirm CTA share this handler. A fast double
    // click can arrive before React commits `isRunning`; the ref closes that
    // gap and remains locked for the full lifetime of an async job.
    if (rerunSubmitInFlightRef.current) return;
    if (!prefsValidation.valid) {
      setWizardOpen(true);
      setStep('config');
      toast.error(
        `Complete required configuration before rerun: ${prefsValidation.missingRequired.join(', ')}`,
      );
      return;
    }
    rerunSubmitInFlightRef.current = true;
    handledAsyncOutcomeRef.current = null;
    setAsyncQueuedRunId(null);
    setAsyncHydrationAttempt(0);
    setAsyncCompletionError(null);
    // Capture BEFORE generation changes store state
    const oldReportId = report.id;
    const oldSiteName = report.siteName;
    const oldVersion = report.version || 1;
    const oldParentId = report.parentReportId || report.id;
    const oldCreatedAt = report.createdAt;
    setOriginalReport(JSON.parse(JSON.stringify(report)));
    setStep('running');
    setIsRunning(true);
    const runId = `rerun-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setCurrentRunId(runId);
    let asyncJobQueued = false;
    try {
      const newVersion = oldVersion + 1;

      // Unified path — load/bootstrap site SED → top up missing sections →
      // generate from SED. Falls back to a name-based site lookup when the
      // report predates the siteId field (legacy reports may lack it).
      const resolvedSiteId =
        report.siteId
        ?? useStore.getState().siteRag.sites.find(
          s => s.name.trim().toLowerCase() === oldSiteName.trim().toLowerCase(),
        )?.id;
      if (!resolvedSiteId) {
        toast.error('Cannot resolve site for this report — recreate the site first.');
        setIsRunning(false);
        return;
      }
      const effectiveEditedPrefs: TypedPreferencesByType[ReportTypeId] = parentReportType === 'custom-brief'
        ? normalizeCustomBriefPrefs(editedPrefs as TypedPreferencesByType['custom-brief']) as TypedPreferencesByType[ReportTypeId]
        : editedPrefs;
      if (parentReportType === 'custom-brief') {
        const customValidation = validateCustomBriefPreferences(
          effectiveEditedPrefs as TypedPreferencesByType['custom-brief'],
        );
        if (!customValidation.valid) {
          setWizardOpen(true);
          setStep('config');
          setIsRunning(false);
          toast.error('Add a brief name and at least one question before rerunning.');
          return;
        }
        try {
          const customPrefs = effectiveEditedPrefs as TypedPreferencesByType['custom-brief'];
          if (!customPrefs?.briefName?.trim()) {
            setIsRunning(false);
            return;
          }
          const existingBrief = (owningSite?.savedCustomBriefs ?? []).find((brief) =>
            brief.name.trim().toLowerCase() === customPrefs.briefName.trim().toLowerCase(),
          );
          await useStore.getState().saveSiteCustomBrief(
            resolvedSiteId,
            customPrefs,
            existingBrief?.id,
          );
          toast.success('Questions saved to site template.');
        } catch (e) {
          setWizardOpen(true);
          setStep('config');
          setIsRunning(false);
          toast.error(`Could not save questions to site template: ${(e as Error).message}`);
          return;
        }
      }
      // Pull typedPreferences / documentRefs / archetype from the parent
      // report so the rerun reproduces the framing instead of silently
      // dropping them. getRerunDefaults walks rerunConfig → top-level
      // for back-compat with reports written before rerunConfig
      // started carrying these fields. generateTypedReport itself ALSO
      // applies a parent-fallback when these are undefined; passing
      // explicitly here makes the call site self-documenting.
      const defaults = getRerunDefaults(report);
      const documentSelection = defaults.documentRefs?.length
        ? { documentRefs: defaults.documentRefs, documentSelectionMode: 'mixed' as const }
        : undefined;
      // Merge the user-edited per-type prefs (from the new Prefs step)
      // back into the typedPreferences map so the rerun reflects the
      // user's edits while preserving any other report-type slots
      // (Workflow Prompt 2 — was silently dropping user edits before).
      //
      // Site_Configuration_Consolidation SC2-6 — `as-is` mode means
      // "regenerate against the site's canonical config", which only
      // makes sense if the user hasn't actually edited the form. If
      // the form is dirty (editedPrefs ≠ initialEditedPrefs OR
      // projectIntent edits via the intent form), the user clearly
      // intends to commit those edits — fall through to the path that
      // forwards them. Defaulting to 'as-is' + auto-detecting dirty
      // keeps the legacy UX (edit form → click Generate) working
      // without forcing the user to first toggle the radio.
      //
      // The radio still drives WHERE those edits land:
      //   - 'as-is' + dirty   → behaves like 'override-only' (the
      //                          most conservative default — the rerun
      //                          gets the edits, site stays untouched).
      //   - 'apply-to-site'   → write-through to site doc.
      //   - 'override-only'   → per-report override only.
      const prefsAreDirty = JSON.stringify(editedPrefs ?? {}) !== JSON.stringify(initialEditedPrefs ?? {});
      const useSiteAsIs = rerunConfigMode === 'as-is' && !prefsAreDirty;
      const typedPrefsForRerun: TypedPreferencesByType = useSiteAsIs
        ? (defaults.typedPreferences ?? {} as TypedPreferencesByType)
        : ({
            ...(defaults.typedPreferences ?? {}),
            [parentReportType]: effectiveEditedPrefs,
          } as TypedPreferencesByType);

      // SC2-6 — 'apply-to-site' branch: persist the user's edits to the
      // site doc BEFORE the rerun fires so future reports inherit. The
      // rerun then reads the site-default through resolveSiteConfig and
      // stamps the same value as *AtGen. Two writes (intent + per-type
      // prefs) ride the same updateSite audit log breadcrumb path the
      // SiteConfigurationPanel uses.
      if (rerunConfigMode === 'apply-to-site') {
        // wf wq07loep2 DATA-LOSS guard (C) — do NOT unconditionally write the
        // wizard's projectIntent back to the site. Only persist when the user
        // actually edited an intent field this session
        // (`userHasEditedIntentRef`, flipped by `setProjectIntentTracked`).
        // An UNTOUCHED rerun must not write intent back at all: writing the
        // seeded value back is what allowed a thin seed to clobber a full
        // live intent. (The store-level guard (B) is the safety net; this
        // skip avoids even attempting the write on an untouched rerun.)
        if (userHasEditedIntentRef.current) {
          try {
            await useStore.getState().updateSiteProjectIntent(resolvedSiteId, projectIntent);
          } catch (e) {
            // This mode generates from canonical site configuration. Enqueuing
            // after a failed save would silently use the previous intent.
            throw new Error(`Could not save project intent: ${(e as Error)?.message ?? 'unknown error'}`);
          }
        }
        try {
          if (effectiveEditedPrefs) {
            await useStore.getState().updateSiteTypedPreferencesDefault(
              resolvedSiteId,
              parentReportType,
              effectiveEditedPrefs,
            );
          }
        } catch (e) {
          throw new Error(`Could not save report preferences: ${(e as Error)?.message ?? 'unknown error'}`);
        }
      }
      // Site_Context SC6 — apply the edit-mode policy:
      //   - 'edit-site'      → persist the new text + freshly-extracted
      //     claims via updateSiteContext FIRST so future reports inherit
      //     and so the in-store site.siteContext is the source of truth.
      //   - 'rerun-override' → DO NOT mutate the site; pass the local
      //     SiteContext snapshot through to generateTypedReport so this
      //     rerun gets the override but the site stays untouched.
      let siteContextOverrideArg: SiteContext | undefined;
      if (siteContext && siteContext.text.trim().length > 0) {
        if (siteContextMode === 'edit-site') {
          try {
            await useStore.getState().updateSiteContext(resolvedSiteId, siteContextText);
          } catch (e) {
            console.warn('[rerun] updateSiteContext failed, proceeding with snapshot:', (e as Error)?.message);
            // Fall through to the override path so the rerun still
            // gets the user's edits even if the persist failed.
            siteContextOverrideArg = siteContext;
          }
        } else {
          siteContextOverrideArg = siteContext;
        }
      }
      // SC2-6 — 'override-only' threads the user's edits as per-report
      // overrides without touching the site doc. *AtGen on the new
      // report matches the override (reproducibility invariant); the
      // site doc keeps producing its prior values for siblings.
      const hasLayoutOverrides = Object.keys(sectionColumnLayouts).length > 0;
      const forceSentimentRescan = parentReportType === 'public-sentiment' && refreshSentimentScan;
      const needsOverrides = rerunConfigMode === 'override-only' || hasLayoutOverrides || forceSentimentRescan;
      const siteConfigOverrides = needsOverrides
        ? {
            ...(rerunConfigMode === 'override-only' ? {
              projectIntentOverride: projectIntent,
              typedPreferencesOverride: effectiveEditedPrefs
                ? ({ [parentReportType]: effectiveEditedPrefs } as TypedPreferencesByType)
                : undefined,
            } : {}),
            ...(hasLayoutOverrides ? { sectionColumnLayouts } : {}),
            // "Refresh sentiment scan": part of the request (and its hash), so
            // the automatic re-dispatch after a site-evidence hold keeps it.
            ...(forceSentimentRescan ? { refreshPublicSentimentScan: true as const } : {}),
          }
        : undefined;
      await generateTypedReport(
        resolvedSiteId,
        report.reportType ?? 'feasibility',
        typedPrefsForRerun,
        'add-another',
        report.id,
        defaults.archetypeOverride,
        documentSelection,
        siteContextOverrideArg,
        siteConfigOverrides,
        runId,
      );

      // Async generation resolves the call above as soon as the Cloud Run job
      // is enqueued. Detect the authoritative job-backed run/outcome and keep
      // this panel in the running state; the completion effect above will use
      // the terminal job's exact reportId. Enqueue failures are recorded as a
      // terminal outcome by the store because generateTypedReport preserves a
      // void API for legacy callers.
      const postGenerationState = useStore.getState().siteRag;
      // Public Sentiment held for site evidence: the store resolved with no job
      // and no outcome on purpose. Waiting for a running build -> it starts
      // automatically (while the page stays open); held -> the store's notice
      // explains the next step. Neither is a failure, nor a missing report.
      const sentimentDisposition = publicSentimentEvidenceDisposition(postGenerationState, {
        siteId: resolvedSiteId,
        reportTypeId: report.reportType ?? 'feasibility',
        runId,
      });
      if (sentimentDisposition === 'waiting') {
        toast.info(SENTIMENT_EVIDENCE_WAITING_NOTICE);
        onClose();
        return;
      }
      if (sentimentDisposition === 'held') {
        // Back to the edits (kept) with the hold notice shown above them.
        setCurrentRunId(null);
        setStep('gaps');
        return;
      }
      const queuedRun = postGenerationState.activeGenerations?.[runId];
      const immediateOutcome = postGenerationState.generationOutcomes?.[runId];
      if (immediateOutcome?.status === 'failed' || immediateOutcome?.status === 'cancelled') {
        throw new Error(
          immediateOutcome.error
          ?? (immediateOutcome.status === 'cancelled'
            ? 'Report generation was cancelled.'
            : 'Report generation could not be queued.'),
        );
      }
      if (queuedRun?.jobId || immediateOutcome?.jobId) {
        asyncJobQueued = true;
        setAsyncQueuedRunId(runId);
        setAsyncHydrationAttempt(0);
        toast.info(`Report v${newVersion} queued — generation will continue in the background.`);
        return;
      }

      // Synchronous fallback only: find the newly-created report for this
      // exact site AND report type. Never fall back to reports[0] — doing so
      // can mutate an unrelated report when generation returned no new row.
      const { siteRag, updateRagReport } = useStore.getState();
      const newReport = siteRag.reports.find(r =>
        r.id !== oldReportId &&
        r.reportType === (report.reportType ?? 'feasibility') &&
        reportBelongsToSite({ id: resolvedSiteId, name: oldSiteName }, r) &&
        new Date(r.createdAt).getTime() > new Date(oldCreatedAt).getTime()
      );

      if (!newReport) {
        throw new Error('Rerun completed without creating a new report for this site and report type.');
      }

      if (newReport.id !== oldReportId) {
        // Intent Prefill IP4 — persist the rerun-edited projectIntent
        // (including any fieldSource provenance stamped by SuggestionChip
        // accepts) onto the new report's rerunConfig. Without this the
        // store falls back to site.projectIntent and the provenance
        // would be lost on save.
        const mergedRerunConfig: NonNullable<SiteRagReport['rerunConfig']> = {
          ...(newReport.rerunConfig ?? {}),
          projectIntent,
        };
        await updateRagReport(newReport.id, {
          version: newVersion,
          minorVersion: 0,
          parentReportId: oldParentId,
          versionNote: versionNote || generateAutoVersionNote(),
          // Workflow Prompt 6 — persist the user's free-text rerun reason
          // as regenerationReason on the new version. Falls back to the
          // auto-note when the user didn't type anything.
          regenerationReason: versionNote.trim() || generateAutoVersionNote(),
          siteId: report.siteId,
          rerunConfig: mergedRerunConfig,
        });
        console.log(`Version ${newVersion} (${newReport.id}) linked to parent ${oldParentId}`);
      }

      toast.success(`Report v${newVersion} generated — review changes`);
      console.log(`[Rerun] New report ${newReport.id}: grid=${!!newReport.gridStudy}, transport=${!!newReport.transportStudy}, connectivity=${!!newReport.connectivityStudy}, score=${newReport.overallScore}`);
      const rerunTargetReportId = newReport.id;
      rerunNavigate(`/sites/${resolvedSiteId}?tab=reports&rerunReportId=${rerunTargetReportId}`);
      // Show diff review dialog
      const latestReport = useStore.getState().siteRag.reports.find(r => r.id === newReport.id);
      if (latestReport) {
        setStep('diff');
        setNewReportForDiff(latestReport);
      } else {
        onClose();
      }
    } catch (err) {
      toast.error(`Rerun failed: ${(err as Error).message}`);
      setStep('gaps');
    } finally {
      if (!asyncJobQueued) {
        rerunSubmitInFlightRef.current = false;
        setIsRunning(false);
      }
    }
  };

  // Rerun Reimagined RR1 — compute the "what changed since v(N)" delta
  // synchronously at panel open so the landing card renders without an
  // extra async hop. We re-read the live site here (not subscribed)
  // because subscribing to siteRag.sites would re-render on every site
  // mutation including unrelated ones. The card is content-cheap so a
  // single computation per render is fine. NB: these hooks must sit
  // BEFORE every early return — React enforces a stable hook order.
  const ownerSite = useMemo(() => {
    const siteId = report.siteId ?? siteForReport(siteRag.sites, report)?.id;
    return siteId ? siteRag.sites.find(s => s.id === siteId) : undefined;
  }, [report.id, report.siteId, report.siteName, siteRag.sites]);

  // Configuration Upgrade (CU5) — list of OTHER report types active on
  // this site, used to filter the cross-type spillover hints in
  // ReportTypePreferencesFields. Reads from the same store the rerun
  // pipeline already consumes so no extra fetch.
  const availableOtherTypes = useMemo<ReportTypeId[]>(() => {
    const ownerSiteId = ownerSite?.id;
    if (!ownerSiteId) return [];
    const allTypes = new Set<ReportTypeId>();
    for (const r of siteRag.reports) {
      if (r.siteId === ownerSiteId && r.reportType && r.reportType !== parentReportType) {
        allTypes.add(r.reportType as ReportTypeId);
      }
    }
    return Array.from(allTypes);
  }, [ownerSite?.id, siteRag.reports, parentReportType]);

  const userApiKeys = useStore(s => s.userApiKeys);
  const configuredProviderIds = useMemo<ApiProviderId[]>(() => {
    const result: ApiProviderId[] = [];
    const keysObj = (userApiKeys ?? {}) as Record<string, string | undefined>;
    for (const provider of Object.values(API_PROVIDERS)) {
      const k = keysObj[provider.userKeyField as unknown as string];
      if (typeof k === 'string' && k.trim().length > 0) result.push(provider.id);
    }
    return result;
  }, [userApiKeys]);

  // We don't have an audit-trail of provider keys present at report
  // generation time. Treat "providers configured on the parent report's
  // rerunConfig" as the prior set when present; otherwise fall back to
  // the currently-configured set (degrades to "no providers added" when
  // the audit trail is missing — avoids false-positive "key added" rows
  // for keys the user has had configured for months).
  const providerIdsAtReportTime = useMemo<ApiProviderId[]>(() => {
    const arr = (report.rerunConfig as unknown as { providerIdsAtGen?: string[] } | undefined)?.providerIdsAtGen;
    if (!Array.isArray(arr)) return configuredProviderIds;
    return arr.filter((id): id is ApiProviderId => id in API_PROVIDERS);
  }, [report.rerunConfig, configuredProviderIds]);

  const rerunDelta = useMemo(
    () => computeRerunDelta({
      report,
      site: ownerSite ?? null,
      configuredProviderIds,
      providerIdsAtReportTime,
      // RDF-1 — thread the live canonical config so the landing card
      // detects Overview / typed-prefs / site-context edits done since
      // this report was generated. We deliberately pass `undefined`
      // (not `null`) for the override slots so the delta helper falls
      // through to `site.projectIntent` etc. — preserving the
      // back-compat path for legacy callers that don't override.
      liveProjectIntent: ownerSite?.projectIntent,
      liveTypedPrefs: ownerSite?.typedPreferencesDefaults,
      liveSiteContext: ownerSite?.siteContext,
      // RDF-2 — pass the loaded SED so `resolvedGaps` can diff against
      // the live gap set. `reviewSed` is lazy-loaded by the existing
      // step==='review' effect; when the user is still on the
      // landing/gaps step it's null and resolvedGaps degrades to 0.
      liveSed: reviewSed,
    }),
    [
      report,
      ownerSite,
      configuredProviderIds,
      providerIdsAtReportTime,
      reviewSed,
    ],
  );
  const deltaEmpty = isRerunDeltaEmpty(rerunDelta);

  // ── Intent Prefill IP2/IP3/IP4 ──────────────────────────────────────────
  // Compute the per-field intent suggestions. The helper is pure — we
  // pass in the live ownerSite, the recent reports for this site
  // (most-recent first; this report excluded), the site's documents,
  // and the active company archetype. Memoised so a typing edit in
  // versionNote doesn't recompute.
  const companyArchetype = useStore(s => s.companyArchetype);
  const recentReportsForSuggester = useMemo<SiteRagReport[]>(() => {
    return siteRag.reports
      .filter(r => r.id !== report.id && (
        reportBelongsToSite(ownerSite ?? { id: report.siteId, name: report.siteName }, r)
      ))
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  }, [siteRag.reports, ownerSite, report.id, report.siteId, report.siteName]);
  const intentSuggestion = useMemo(() => {
    return suggestProjectIntent({
      site: ownerSite ?? undefined,
      recentReports: recentReportsForSuggester,
      documents: ownerSite?.documents ?? [],
      archetype: ownerSite?.archetypeOverride ?? companyArchetype ?? undefined,
    });
  }, [ownerSite, recentReportsForSuggester, companyArchetype]);

  // Rerun Reimagined RR6 — auto-generated version note. Derived from
  // the RR1 delta + a config-diff list (Step-2 intent fields + Step-3
  // typed-pref deltas the user has edited this session). Stored in
  // local state with a "user-edited" flag so a free-text edit isn't
  // clobbered by a subsequent delta recalculation.
  const autoVersionNote = useMemo(() => {
    const changedFields: string[] = [];
    const origIntent = report.rerunConfig?.projectIntent;
    if (origIntent) {
      const origWorkload = effectiveWorkloadProfile(origIntent);
      const newWorkload = effectiveWorkloadProfile(projectIntent);
      if (origWorkload !== newWorkload) changedFields.push('workload');
      if (origIntent.nuclearInterest !== projectIntent.nuclearInterest) changedFields.push('nuclear');
      if (origIntent.priority !== projectIntent.priority) changedFields.push('priority');
    }
    for (const d of typedPrefDeltas(
      initialEditedPrefs as Record<string, unknown>,
      editedPrefs as Record<string, unknown>,
    )) {
      changedFields.push(d.label);
    }
    return generateVersionNote(
      rerunDelta,
      { changedFields },
      {
        pinnedSedVersion: report.sedVersionUsed,
        liveSedVersion: ownerSite?.sedVersion,
      },
    );
  }, [rerunDelta, report, projectIntent, initialEditedPrefs, editedPrefs, ownerSite?.sedVersion]);
  // Pre-fill the version note when the user hasn't typed anything yet.
  useEffect(() => {
    if (versionNoteUserEdited) return;
    setVersionNote(autoVersionNote);
  }, [autoVersionNote, versionNoteUserEdited]);

  // Rerun Reimagined RR3 + RWX-3 — derive provider posture from the
  // report. The provider→dataset map + the unavailable-from-references
  // detection used to live inline here; RWX-3 lifts it into
  // `src/lib/rerunPreflight.ts` so the chip, the step body, and the
  // confirm-step recap all consume one canonical signal (W2 from the
  // 2026-05-17 walkthrough). The local memo just wraps the helper to
  // keep referential stability for ProviderPostureRow's items prop.
  const providerPosture = useMemo<ProviderPostureItem[]>(
    () => deriveProviderPostureFromReport(report),
    [report],
  );

  // RWX-3 — single computed preflight summary every messaging surface
  // reads. Replaces the inline "Provider data fresh — nothing to
  // resolve before rerunning." literal that lived in the step body
  // (and could drift from the chip's count). Same shape every surface
  // consumes: `headline` for chip/pill, `detail` for step body.
  const preflightSummary = useMemo(
    () => computeRerunPreflight(report, { reportType: parentReportType }),
    [report, parentReportType],
  );

  // UXS-1 — the visible "Advanced — gaps · configuration · review"
  // disclosure has been removed (live-prod Redhead 2026-05-18 feedback:
  // it duplicated the canonical Overview / Site Evidence / Documents
  // edit surfaces and made the rerun feel like a wizard-within-a-wizard).
  //
  // The wizard step content (Preflight / Configuration / Confirm) is
  // still mounted in this component because gap resolution + per-rerun
  // overrides need it — but it now only renders when explicitly invoked:
  //   - initial state with unresolved gaps → wizard auto-opens on 'gaps'
  //     so the user can resolve them
  //   - RerunDeltaCard's "Resolve gaps" / "Open config" CTAs open it
  //   - deep-link `?step=` arriving on a non-default step opens it
  //
  // The happy path (delta non-empty + no gaps) shows the single-screen
  // landing card + "Run with these changes" CTA + the UXS-1 deep-link
  // row to the source-of-truth surfaces (Overview · Site Evidence ·
  // Coverage Matrix). No "expand for more" affordance.
  const happyPathAvailable = !deltaEmpty && rerunDelta.pendingGaps === 0;
  const [wizardOpen, setWizardOpen] = useState(false);

  useUrlSearchParamSync('step', open && wizardOpen ? step : null);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    const requestedRerun = params.get('rerun');
    const requestedStep = params.get('step');
    const requestedFocus = params.get('focus');
    const normalizedStep =
      requestedStep === 'prefs' ? 'config'
        : requestedStep === 'gaps' || requestedStep === 'config' || requestedStep === 'review'
          ? requestedStep
          : null;

    if (requestedRerun === versionLabel && normalizedStep) {
      setWizardOpen(true);
      setStep(normalizedStep);
      return;
    }
    if (requestedRerun === versionLabel && requestedFocus === 'questions' && parentReportType === 'custom-brief') {
      setWizardOpen(true);
      setStep('config');
      return;
    }
    setWizardOpen(!happyPathAvailable);
  }, [open, happyPathAvailable, parentReportType, versionLabel]);

  if (!open) return null;

  // Show diff review dialog after rerun
  if (step === 'diff' && newReportForDiff) {
    return <DiffReviewDialog oldReport={originalReport} newReport={newReportForDiff} open={true} onClose={() => {
      // Land the user on the site's Reports tab so the new report shows up
      // alongside the rest, instead of dropping straight into the new
      // report viewer. Clearing activeReportId lets SiteRagEngine's view
      // selector flip back to 'site'; the event tells the engine to set
      // its `landingInitialTab` to 'reports' on the next site mount.
      useStore.getState().setActiveRagReport(null);
      if (report.siteId) {
        window.dispatchEvent(
          new CustomEvent('datacrate:rerun-completed', {
            detail: { siteId: report.siteId, newReportId: newReportForDiff.id },
          }),
        );
      }
      onClose();
    }} />;
  }

  const unresolvedCritical = gaps.filter(g => !g.resolved && !g.ignored && g.impact === 'critical').length;

  // RP1 — inline diff chip summary. Drives the chip caption next to the
  // primary CTA + the popover's grouped breakdown (Documents / Report-type
  // prefs / Site project intent). Hidden when totalChanges === 0; the
  // RerunDeltaCard's empty-delta path takes over there.
  const rerunDiffSummary = computeRerunDiff({
    report,
    editedDocs: uploadedDocs,
    excludedDocIds,
    editedPrefs,
    baselinePrefs: initialEditedPrefs,
    projectIntent,
  });
  const rerunDiffVersionLabel = `v${report.version || 1} → v${(report.version || 1) + 1}`;

  // RP2 — the old `sidebarRows: DiffRow[]` composition (which fed the
  // now-deleted RerunDiffSidebar) is gone. The diff lives inline on
  // the Run CTA via RerunDiffChip (RP1), driven by the
  // `rerunDiffSummary` above. RR1 delta signals (SED bump, provider
  // keys, resolved gaps) still surface on the RerunDeltaCard landing
  // surface — they don't need a second redundant rendering.

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      {/* RP2 — responsive modal sizing. The earlier RR5 2-column
           layout (main + diff sidebar) is collapsed to single-column;
           the sidebar is gone and the diff lives inline as a chip on
           the primary CTA. The modal stays at 920px max-width — the
           wider Configuration step (banded layouts + embedded prefs
           form) still uses every pixel. */}
      <div className="bg-background w-[calc(100vw-2rem)] max-w-[920px] lg:w-[920px] max-h-[85vh] flex flex-col shadow-2xl border border-border overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex-1 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">Rerun Analysis</h2>
            <p className="type-meta text-text-secondary uppercase tracking-widest mt-0.5">
              {report.siteName} — v{report.version || 1}.{report.minorVersion || 0} → v{(report.version || 1) + 1}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
        </div>

        {step === 'running' ? (
          <>
            <GenerationProgress
              progressStage={scopedProgressStage || 'Initializing…'}
              progress={scopedProgress}
              heartbeatAt={currentRunHeartbeatAt}
              startedAt={currentRun?.startedAt}
              estimatedDurationMs={currentRun?.estimatedDurationMs}
              title={`Generating v${(report.version || 1) + 1}`}
              error={scopedError || null}
              onContinueInBackground={() => {
                onClose();
                toast.info("Report generating in background — you'll be notified when done");
              }}
            />
            {/* Recovery CTA when generation fails — distinct from the
                Continue-in-background button so the user can step back
                to the config step and retry without losing edits. */}
            {scopedError && (
              <div className="text-center pb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 type-meta text-danger"
                  onClick={() => {
                    rerunSubmitInFlightRef.current = false;
                    handledAsyncOutcomeRef.current = null;
                    setAsyncQueuedRunId(null);
                    setCurrentRunId(null);
                    setAsyncCompletionError(null);
                    setStep('gaps');
                    setIsRunning(false);
                  }}
                >
                  Back to Configuration
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 space-y-4">
            {sentimentEvidenceHold ? (
              <SentimentEvidenceHoldNotice
                hold={sentimentEvidenceHold}
                title="Public Sentiment has not started"
                data-testid="rerun-sentiment-evidence-hold"
              />
            ) : null}
            {parentReportType === 'public-sentiment' ? (
              <SentimentScanRefreshOption
                checked={refreshSentimentScan}
                onChange={setRefreshSentimentScan}
                scanFetchedAt={reviewSed?.publicSentiment?.fetchedAt ?? null}
                disabled={isRunning}
              />
            ) : null}
            {/* Rerun Reimagined RR1 — "What changed since v(N)" landing
                 card. Renders first, above the tab strip, so the user
                 sees the actionable delta before being asked to walk
                 the wizard. Empty-delta state suggests opening
                 Configuration; unresolved-gaps state jumps to step 1. */}
            <RerunDeltaCard
              delta={rerunDelta}
              pinnedSedVersion={report.sedVersionUsed}
              liveSedVersion={ownerSite?.sedVersion}
              onResolveGaps={() => { setWizardOpen(true); setStep('gaps'); }}
              onOpenConfig={() => { setWizardOpen(true); setStep('config'); }}
              // SCR-3 (row 0.99291) — surface the "Pulled from live site"
              // banner when the freshest-wins resolver landed on the
              // live-site branch for ANY of the three site-config
              // surfaces. The `[↩ Use original]` button only makes sense
              // for the projectIntent surface today (the snapshot revert
              // for siteContext + typedPrefs is more complex — deferred);
              // we still light the banner so the user knows live values
              // are in play.
              scrShowPulledFromSiteBanner={Boolean(
                owningSite?.siteConfigUpdatedAt &&
                report.createdAt &&
                new Date(owningSite.siteConfigUpdatedAt).getTime() > new Date(report.createdAt).getTime() &&
                (scrSource === 'live-site' || scrSiteContextSource === 'live-site' || scrTypedPrefsSource === 'live-site'),
              )}
              scrReportGeneratedAt={report.createdAt}
              scrSiteConfigUpdatedAt={owningSite?.siteConfigUpdatedAt}
              onScrRestoreOriginal={scrSource === 'live-site' ? scrRestoreOriginal : undefined}
            />
            {/* Rerun Reimagined RR2 — happy-path "Run with these changes"
                 CTA. Renders when the delta has actionable changes AND
                 there are no unresolved gaps blocking the run. Wires to
                 the same handleRerun() the Review step uses, so the diff
                 + audit pipeline is untouched.
                 UXS-1 — gated on !wizardOpen so the simple footer hides
                 when the wizard is open. The visible Advanced toggle is
                 gone; wizard state is now driven by initial-gap detection
                 + the RerunDeltaCard deep-link CTAs + ?step= URL params,
                 not a user-toggled disclosure. */}
            {!wizardOpen && !deltaEmpty && rerunDelta.pendingGaps === 0 && (
              <div
                className="flex flex-col items-stretch gap-1.5"
                data-testid="rerun-happy-path"
              >
                {/* RP2 — single action footer for the rerun-canvas happy
                     path. Save & exit (secondary outline) on the left,
                     Run with these changes (primary, prefixed by the
                     RP1 diff chip) on the right. Save & exit hits
                     onClose — its semantics are the SC2-6
                     'apply-to-site' path the three-way radio surfaces
                     (updateSite + updateSiteProjectIntent +
                     updateSiteTypedPreferencesDefault when the user has
                     picked apply-to-site). It does NOT call
                     handleRerunRequest, so the regen cost is skipped. */}
                <div className="flex items-center justify-between gap-2" data-testid="rerun-action-footer">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 type-meta"
                    onClick={onClose}
                    data-testid="rerun-save-and-exit"
                  >
                    Save &amp; exit
                  </Button>
                  <div className="flex items-center gap-2">
                    {/* RP1 — inline diff chip sits to the left of the
                         primary CTA. Click opens a popover with the
                         full breakdown grouped by Documents /
                         Report-type prefs / Site project intent.
                         Hidden when there are no changes; the
                         empty-delta auto-skip path takes over via the
                         RerunDeltaCard. */}
                    <RerunDiffChip
                      summary={rerunDiffSummary}
                      versionLabel={rerunDiffVersionLabel}
                    />
                    <Button
                      size="sm"
                      className="h-10 type-meta font-bold"
                      onClick={() => void handleRerunRequest()}
                      disabled={isRunning || isPreflightRefreshing}
                      data-testid="rerun-run-with-changes"
                    >
                      <Zap className={`w-3.5 h-3.5 mr-2 ${isRunning ? 'animate-pulse' : ''}`} aria-hidden="true" />
                      Run with these changes
                    </Button>
                  </div>
                </div>
                {/* RWX-1 — state-aware subtitle. The static "No edits
                     needed" string contradicted the WHAT CHANGED card
                     above (W3 from the 2026-05-17 walkthrough): the
                     card surfaces N applied insights + configChanged
                     while the subtitle simultaneously claimed there's
                     nothing to commit. Pull the same signals the
                     WHAT CHANGED card reads (rerunDelta) so the copy
                     always matches what the user sees. */}
                <span className="type-body text-text-muted text-center" data-testid="rerun-happy-path-subtitle">
                  {(() => {
                    const insightsApplied = rerunDelta.appliedFindingsCount ?? 0;
                    const parts: string[] = [];
                    if (insightsApplied > 0) {
                      parts.push(`${insightsApplied} insight${insightsApplied === 1 ? '' : 's'} queued`);
                    }
                    if (rerunDelta.configChanged) parts.push('configuration edited');
                    if (rerunDelta.resolvedGaps > 0) {
                      parts.push(`${rerunDelta.resolvedGaps} gap${rerunDelta.resolvedGaps === 1 ? '' : 's'} resolved`);
                    }
                    return parts.length > 0
                      ? `${parts.join(', ')} — will regenerate v${(report.version || 1) + 1} against the current state.`
                      : `Generates v${(report.version || 1) + 1} against the current state.`;
                  })()}
                </span>
              </div>
            )}
            {/* UXS-1 — visible Advanced disclosure removed. Replaced
                 below with a deep-link row to the canonical source-of-
                 truth surfaces (Overview · Site Evidence · Documents)
                 so users edit settings AT THE SOURCE rather than via
                 a duplicated wizard surface inside this modal. The
                 wizard step content is still mounted (we still need
                 it for gap resolution + per-rerun overrides) and is
                 controlled programmatically by `wizardOpen`. */}
            {!wizardOpen && happyPathAvailable && !!report.siteId && (
              <div
                className="border-t border-border-subtle pt-3 mt-1 space-y-2"
                data-testid="rerun-source-of-truth-deep-links"
              >
                <div className="type-body text-text-muted uppercase tracking-widest">
                  Need to adjust settings before rerunning? Edit them at the source:
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`/?site=${encodeURIComponent(report.siteId)}&tab=overview&focus=intent`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="type-body font-bold uppercase tracking-widest text-text-secondary hover:text-primary border border-border px-2 py-1 hover:border-primary"
                    data-testid="rerun-edit-deep-link-intent"
                  >
                    Overview → Site Project Intent
                  </a>
                  <a
                    href={`/?site=${encodeURIComponent(report.siteId)}&tab=overview&focus=prefs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="type-body font-bold uppercase tracking-widest text-text-secondary hover:text-primary border border-border px-2 py-1 hover:border-primary"
                    data-testid="rerun-edit-deep-link-prefs"
                  >
                    Overview → Report Preferences
                  </a>
                  <a
                    href={`/?site=${encodeURIComponent(report.siteId)}&tab=evidence&focus=coverage`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="type-body font-bold uppercase tracking-widest text-text-secondary hover:text-primary border border-border px-2 py-1 hover:border-primary"
                    data-testid="rerun-edit-deep-link-coverage"
                  >
                    Site Evidence → Coverage Matrix
                  </a>
                </div>
              </div>
            )}
            {wizardOpen && (
              <>
            {/* Rerun Reimagined RR3 — provider posture row.
                 Replaces the binary "Provider data fresh" pill with
                 an information-dense summary (verified / unavailable
                 counts, plus relevance-aware "outside relevance"
                 classification using PROJECTION_BY_TYPE so providers
                 whose downstream sections are all outside the report
                 type's projection don't trigger a false-warning state).
                 The legacy data-testids stay registered so existing
                 tests still pin against them. */}
            {!initialGapsExist && step !== 'gaps' && (
              <div data-testid="rerun-provider-fresh-pill">
                <ProviderPostureRow
                  reportType={parentReportType}
                  items={providerPosture}
                  onView={() => setStep('gaps')}
                />
                {/* Legacy "View provider details" hook — kept so
                     existing tests + deep-links still hit the same
                     handler. Visually subsumed by the new row's
                     "View →" button above. */}
                <button
                  type="button"
                  onClick={() => setStep('gaps')}
                  className="sr-only"
                  data-testid="rerun-provider-fresh-view-details"
                >
                  View provider details
                </button>
              </div>
            )}
            {/* RR5-1 — three-step strip: Preflight · Configuration · Confirm.
                  The merged Configuration step folds the legacy "Project intent"
                  + "Report-type preferences" into one screen with two
                  clearly-labelled sub-sections (RerunConfigurationStep). The
                  legacy `rerun-step-prefs` testid is retained as an sr-only
                  alias so existing per-type panel tests + deep-links into the
                  former Step-3 still resolve to the same merged surface. */}
            <div className="flex gap-1">
              <button onClick={() => setStep('gaps')}
                className={`flex-1 py-2 type-meta font-bold uppercase tracking-widest border-b-2 transition-colors ${step === 'gaps' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                data-testid="rerun-step-gaps">
                1. Preflight
              </button>
              <button onClick={() => setStep('config')}
                className={`flex-1 py-2 type-meta font-bold uppercase tracking-widest border-b-2 transition-colors ${step === 'config' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                data-testid="rerun-step-config">
                2. Configuration
              </button>
              <button onClick={() => setStep('config')}
                className="sr-only"
                data-testid="rerun-step-prefs"
                aria-hidden="true"
                tabIndex={-1}
              >
                Report-type settings (merged into Configuration — RR5-1)
              </button>
              <button onClick={() => setStep('review')}
                className={`flex-1 py-2 type-meta font-bold uppercase tracking-widest border-b-2 transition-colors ${step === 'review' ? 'border-primary text-primary' : 'border-transparent text-text-secondary'}`}
                data-testid="rerun-step-review">
                3. Confirm
              </button>
            </div>

            {step === 'gaps' && (() => {
              // Workflow Prompt 6 — type-specific bucket grouping. Gaps carry their
              // original index so action handlers (which mutate by index) keep working.
              const indexedGaps = gaps.map((gap, idx) => ({ ...gap, __idx: idx }));
              const groups = gapCategorizer
                ? gapCategorizer(indexedGaps as DataGapItem[])
                  .map(g => ({ label: g.label, items: g.items as (DataGapItem & { __idx: number })[] }))
                : [{ label: 'All gaps', items: indexedGaps }];
              const showGroupHeaders = groups.length > 1;

              const renderGap = (gap: DataGapItem & { __idx: number }) => {
                const idx = gap.__idx;
                const classification = classifyGap({ reason: gap.description, providerId: gap.providerId });
                return (
                  <div key={idx} className={`p-3 border space-y-2 ${
                    gap.resolved ? 'border-success/40 bg-success/15/50' :
                    gap.ignored ? 'border-border bg-muted/50 opacity-60' :
                    gap.impact === 'critical' ? 'border-danger/40 bg-danger/15/50' : 'border-warning/40 bg-warning/15/50'
                  }`}>
                    <div className="flex items-start gap-2">
                      {gap.resolved ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
                      ) : gap.ignored ? (
                        <X className="w-3.5 h-3.5 text-text-secondary shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${gap.impact === 'critical' ? 'text-danger' : 'text-warning'}`} />
                      )}
                      <div className="flex-1">
                        <div className={`type-body font-bold uppercase tracking-widest ${gap.ignored ? 'text-text-secondary line-through' : 'text-foreground'}`}>{gap.field}</div>
                        <div className={`type-meta mt-0.5 ${gap.ignored ? 'text-text-secondary' : 'text-text-muted'}`}>{gap.description}</div>
                        {gap.resolved && gap.resolution && (
                          <div className="type-meta text-success mt-0.5">{gap.resolution}</div>
                        )}
                        {gap.ignored && (
                          <div className="type-meta text-text-secondary mt-0.5">Marked as not relevant</div>
                        )}
                      </div>
                      <span className={`type-meta font-bold uppercase tracking-widest px-1.5 py-0.5 ${
                        gap.ignored ? 'bg-surface-3 text-text-secondary' :
                        gap.impact === 'critical' ? 'bg-danger/15 text-danger' : gap.impact === 'significant' ? 'bg-warning/15 text-warning' : 'bg-surface-3 text-text-muted'
                      }`}>{gap.ignored ? 'ignored' : gap.impact}</span>
                    </div>
                    {!gap.ignored && classification.kind === 'apiKey' && !gap.resolved && (
                      <div className="pl-5 type-meta uppercase tracking-widest text-text-muted">
                        Looks like an API key issue{classification.httpStatus ? ` (${classification.httpStatus})` : ''} — {classification.provider.displayName}
                      </div>
                    )}
                    {!gap.ignored && (
                      <div className="flex gap-2 pl-5 flex-wrap">
                        {classification.kind === 'apiKey' && !gap.resolved && (
                          <>
                            <button
                              onClick={() => setKeyDialog({ provider: classification.provider, gapIdx: idx })}
                              className="type-meta font-bold uppercase tracking-widest text-primary px-2 py-1 border border-primary hover:bg-primary/10 flex items-center gap-1"
                            >
                              <Key className="w-2.5 h-2.5" /> Test &amp; Link Key
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await notifyAdmins(classification.provider, { gapReason: gap.description });
                                  toast.success('Admins notified — they can set the org-level key.');
                                } catch (e) {
                                  toast.error(`Failed to notify admins: ${(e as Error).message}`);
                                }
                              }}
                              className="type-meta font-bold uppercase tracking-widest text-text-secondary hover:text-primary px-2 py-1 border border-border hover:bg-state-hover flex items-center gap-1"
                            >
                              <Send className="w-2.5 h-2.5" /> Notify Admins
                            </button>
                          </>
                        )}
                        <label className="block">
                          <input type="file" multiple accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.md,.json,.rtf,.pptx,.ppt,.jpg,.jpeg,.png,.gif,.kmz,.kml,.eml,.msg,.url,.log,.xml,.html" className="hidden"
                            onChange={(e) => handleUploadForGap(idx, e.target.files)} disabled={isProcessingDoc} />
                          <div className="type-meta font-bold uppercase tracking-widest text-text-secondary hover:text-primary px-2 py-1 border border-border cursor-pointer hover:bg-state-hover flex items-center gap-1">
                            <Upload className="w-2.5 h-2.5" /> {gap.resolved ? 'Add More Documents' : 'Upload Document'}
                          </div>
                        </label>
                        {!gap.resolved && (
                          <button onClick={() => handleManualInput(idx)}
                            className="type-meta font-bold uppercase tracking-widest text-text-secondary hover:text-primary px-2 py-1 border border-border hover:bg-state-hover flex items-center gap-1">
                            <FileText className="w-2.5 h-2.5" /> Input via Evidence Data
                          </button>
                        )}
                        {!gap.resolved && (
                          <button onClick={() => setGaps(prev => prev.map((g, i) => i === idx ? { ...g, ignored: true } : g))}
                            className="type-meta font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-2 py-1 border border-border hover:bg-muted flex items-center gap-1">
                            <X className="w-2.5 h-2.5" /> Not Relevant
                          </button>
                        )}
                        {gap.resolved && (
                          <button onClick={() => setGaps(prev => prev.map((g, i) => i === idx ? { ...g, resolved: false, resolution: undefined } : g))}
                            className="type-meta text-text-secondary hover:text-text-primary underline px-2 py-1">
                            Undo
                          </button>
                        )}
                      </div>
                    )}
                    {gap.ignored && (
                      <div className="pl-5">
                        <button onClick={() => setGaps(prev => prev.map((g, i) => i === idx ? { ...g, ignored: false } : g))}
                          className="type-meta text-text-secondary hover:text-text-primary underline">
                          Undo
                        </button>
                      </div>
                    )}
                  </div>
                );
              };

              const hasAnyGaps = initialGapsExist || gaps.length > 0;
              return (
                <div className="space-y-3">
                  {/* Reader Polish Prompt 5 — two-column status grid
                       reconciles "providers verified" vs. "documents
                       missing" so the reader sees both surfaces in
                       one glance.
                       Rerun Wizard Coherence W7 — the grid and the
                       "Provider data fresh" affordance overlapped on the
                       no-gaps path. Now they're mutually exclusive: gaps
                       ⇒ the grid (+ per-gap list); no gaps ⇒ a single
                       compact "fresh" confirmation, no grid. The
                       above-the-tabs `rerun-provider-fresh-pill` covers
                       the same outcome when the user is on a later step. */}
                  {hasAnyGaps ? (
                    <RerunProviderDataStatus
                      report={report}
                      providerGapCount={gaps.filter(g => !g.resolved && !g.ignored).length}
                      onUploadDoc={() => setShowLibraryPicker(true)}
                      onReviewFindings={onClose}
                    />
                  ) : (
                    <div
                      className={`flex items-center gap-1.5 px-3 py-2 border text-[10px] ${
                        preflightSummary.state === 'fresh'
                          ? 'border-success/40 bg-success/10 text-success'
                          : 'border-warning/40 bg-warning/10 text-warning'
                      }`}
                      data-testid="rerun-provider-fresh-confirm"
                      data-preflight-state={preflightSummary.state}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                      {/* RWX-3 — single source-of-truth string from
                           computeRerunPreflight. The chip above, this
                           body, and the confirm-step recap all consume
                           the same `.detail` so the three preflight
                           messages can never diverge (W2 fix). */}
                      {preflightSummary.detail}
                    </div>
                  )}
                  {gaps.length > 0 && (
                    <>
                      <div className="type-meta text-text-secondary uppercase tracking-widest">
                        Resolve data gaps before rerunning for more accurate results. Upload documents or input values.
                      </div>
                      {groups.map(group => (
                        <div key={group.label} className="space-y-2">
                          {showGroupHeaders && (
                            <div className="type-section text-text-primary pt-1">
                              {group.label} <span className="text-text-muted">· {group.items.length}</span>
                            </div>
                          )}
                          {group.items.map(renderGap)}
                        </div>
                      ))}
                    </>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button size="sm" className="h-8 type-body uppercase tracking-widest" onClick={() => setStep('config')}>
                      {unresolvedCritical > 0 ? `Continue with ${unresolvedCritical} unresolved` : 'Next'} <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* RR5-1 — merged Configuration step. The legacy split between
                  "Project intent" (Step 2) and "Report-type preferences"
                  (Step 3) is collapsed into a single screen with two
                  clearly-labelled sub-sections rendered inline by
                  RerunConfigurationStep:
                    A. Site project intent — workload / nuclear / priority,
                       wrapped in FieldBand banding (RR5-2).
                    B. Report-type preferences — typed prefs for this
                       report's type, banded identically to A.
                    C. Documents — upload + library picker.
                  The version-note input previously here moved to the
                  Confirm step (RR5-4). */}
            {step === 'config' && (
              <div className="space-y-4" data-testid="rerun-configuration-step">
                {/* RWX-2 — single-screen SSOT-aligned Configuration step.
                     The outer "How should this rerun use the site's
                     configuration?" 3-radio (SC2-6) is gone — per SSOT
                     (row 0.99187), per-report overrides for shared
                     fields are retired. Site Project Intent /
                     Site Context / typed-pref defaults all live on the
                     site doc; the rerun reads them through and writes
                     edits BACK to the site doc (rerunConfigMode pins
                     to 'apply-to-site' permanently — see useState
                     initialiser above). The inner Site Context
                     mode toggle is similarly hidden (no `mode` prop
                     passed below; the SiteContextInput's toggle is
                     gated on the prop being defined). Users still
                     get a one-line explainer here that maps to the
                     SSOT narrative the rest of the platform tells. */}
                <div
                  className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[10px] text-text-secondary leading-relaxed"
                  data-testid="rerun-config-ssot-banner"
                >
                  <span className="font-semibold text-text-primary">Site Project Intent is the single source of truth.</span>{' '}
                  Edits below apply to every future report on this site. To browse the full configuration surface, jump to the site's <button
                    type="button"
                    onClick={() => {
                      // Lightweight EditAtSiteLink shim — closes the
                      // rerun modal and re-opens the site landing page
                      // on the Overview tab. RWX-4 hardens this with a
                      // ?siteConfig=<section> deep-link that auto-opens
                      // the right SiteConfigurationPanel section.
                      onClose();
                      try {
                        const sp = new URLSearchParams(window.location.search);
                        sp.set('tab', 'overview');
                        // RWX-2/4 sentinel — the `siteConfig` param is
                        // read by SiteConfigurationPanel's URL-mount
                        // probe (RWX-4). Defaults to 'intent' since
                        // that's the field-rich section.
                        sp.set('siteConfig', 'intent');
                        const next = `${window.location.pathname}?${sp.toString()}`;
                        window.history.pushState({}, '', next);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      } catch { /* best-effort URL push */ }
                    }}
                    className="text-primary underline hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    data-testid="rerun-config-edit-at-site-link"
                  >Overview → Site Project Intent</button>.
                </div>

                {/* SRW-2 (2026-05-19) — Site Context is the most-typed
                     SHARED field; if anything it deserves the strongest
                     "site is the source" affordance. SC6 had mounted
                     `<SiteContextInput>` here, which is a multi-line
                     editable textarea PLUS an extracted-claims editor
                     PLUS a conflicts panel — that's ~600px of inline
                     site-canonical editing surface in the rerun wizard,
                     exactly the SSOT regression SRW exists to fix.
                     Replaced with a read-only excerpt of the canonical
                     site context + claim count chip + "Edit at site
                     level →" deep-link to the Site Context tab. The
                     handleRerun path still reads `siteContextText` /
                     `siteContext` from local state (seeded from the
                     SITE doc), so the persistence behaviour is
                     unchanged — we just don't allow inline edits. */}
                {(() => {
                  const ctxText = (siteContextText ?? '').trim();
                  const claimCount = siteContext?.claims?.length ?? 0;
                  const siteContextHref = owningSite?.id
                    ? `/?site=${encodeURIComponent(owningSite.id)}&tab=overview&focus=context#site-context`
                    : undefined;
                  return (
                    <section
                      data-testid="config-step-site-context-summary"
                      className="border border-border p-3 bg-state-hover/50 space-y-2"
                    >
                      <header className="flex items-center justify-between gap-2">
                        <h3 className="type-body font-bold uppercase tracking-widest text-text-secondary">
                          What we know about this site
                        </h3>
                        {siteContextHref ? (
                          <a
                            href={siteContextHref}
                            data-testid="config-step-site-context-edit-link"
                            className="type-body uppercase tracking-widest text-primary hover:text-primary/80 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                          >
                            Edit at site level →
                          </a>
                        ) : (
                          <span
                            data-testid="config-step-site-context-edit-link-disabled"
                            className="type-body uppercase tracking-widest text-text-muted"
                            title="Site id unavailable — edit Site Context from the site landing page."
                          >
                            Edit at site level
                          </span>
                        )}
                      </header>
                      {ctxText.length > 0 ? (
                        <p
                          className="type-body text-text-secondary line-clamp-3 font-mono whitespace-pre-wrap break-words"
                          data-testid="config-step-site-context-summary-text"
                        >
                          {ctxText}
                        </p>
                      ) : (
                        <p
                          className="type-body text-text-muted italic"
                          data-testid="config-step-site-context-summary-empty"
                        >
                          — No site context yet —
                        </p>
                      )}
                      {claimCount > 0 && (
                        <p
                          className="type-meta text-text-muted"
                          data-testid="config-step-site-context-summary-claim-count"
                        >
                          {claimCount} extracted claim{claimCount === 1 ? '' : 's'} · open at site level to review
                        </p>
                      )}
                    </section>
                  );
                })()}

                <RerunConfigurationStep
                  report={report}
                  reportType={parentReportType}
                  // SRW-1 (2026-05-19) — thread the canonical site id
                  // down so the read-only Site Project Intent summary
                  // card can deep-link "Edit at site level →" to the
                  // Site Configuration Panel. owningSite is the same
                  // memo the panel uses to resolve site context.
                  siteId={owningSite?.id ?? report.siteId ?? undefined}
                  editedPrefs={editedPrefs}
                  initialEditedPrefs={initialEditedPrefs}
                  setEditedPrefs={(next) => setEditedPrefs(next)}
                  availableOtherTypes={availableOtherTypes}
                  archetype={
                    report.companyArchetypeOverride
                      ?? report.company?.archetype
                      ?? ownerSite?.archetypeOverride
                  }
                  // dam={undefined} — wired here for the CU6 stub; the
                  // store doesn't currently expose a per-rerun DAM yet,
                  // so the suggestion panel falls through to a no-op.
                  companyContext={
                    report.company
                      ? {
                          id: report.company.id,
                          name: report.company.name,
                          archetype:
                            report.companyArchetypeOverride
                              ?? report.company.archetype,
                          archetypeLabel: report.company.archetypeLabel,
                          archetypeNote: report.company.archetypeNote,
                        }
                      : undefined
                  }
                  customBriefSite={
                    owningSite
                      ? {
                          id: owningSite.id,
                          savedCustomBriefs: owningSite.savedCustomBriefs,
                        }
                      : undefined
                  }
                  onSaveCustomBrief={
                    owningSite
                      ? (preferences, existingId) =>
                          useStore.getState().saveSiteCustomBrief(owningSite.id, preferences, existingId)
                      : undefined
                  }
                  onDeleteCustomBrief={
                    owningSite
                      ? (briefId) => useStore.getState().deleteSiteCustomBrief(owningSite.id, briefId)
                      : undefined
                  }
                  projectIntent={projectIntent}
                  // SCR-2 — the tracked setter flips
                  // `userHasEditedIntentRef` so a mid-flight live-site
                  // update can't clobber an in-progress user edit.
                  setProjectIntent={setProjectIntentTracked}
                  uploadedDocs={uploadedDocs}
                  setUploadedDocs={setUploadedDocs}
                  excludedDocIds={excludedDocIds}
                  setExcludedDocIds={setExcludedDocIds}
                  isProcessingDoc={isProcessingDoc}
                  docProgress={docProgress}
                  showLibraryPicker={showLibraryPicker}
                  setShowLibraryPicker={setShowLibraryPicker}
                  filterValidFiles={filterValidFiles}
                  onBatchUpload={handleBatchUpload}
                  suggestion={intentSuggestion}
                  sectionColumnLayouts={sectionColumnLayouts}
                  setSectionColumnLayouts={setSectionColumnLayouts}
                  onBack={() => setStep('gaps')}
                  onNext={() => setStep('review')}
                />
                {/* Legacy `rerun-prefs-step` + `rerun-prefs-next` testids
                     are kept as sr-only aliases so per-type panel tests
                     (which click `rerun-prefs-next`) still pass against
                     the merged step (RR5-1). */}
                <div data-testid="rerun-prefs-step" className="sr-only">
                  Merged into Configuration step (RR5-1).
                </div>
                {!prefsValidation.valid && prefsValidation.missingRequired.length > 0 && (
                  <div
                    className="type-body text-warning rounded-md border border-warning/30 bg-warning/5 px-2 py-1.5"
                    data-testid="rerun-prefs-missing-required"
                  >
                    Required: {prefsValidation.missingRequired.join(' · ')}
                  </div>
                )}
                {siteContextHasBlockingConflicts && (
                  <div
                    className="type-body text-danger rounded-md border border-danger/30 bg-danger/5 px-2 py-1.5"
                    data-testid="rerun-site-context-blocking"
                  >
                    Resolve or acknowledge HIGH-severity site-context conflicts before continuing.
                  </div>
                )}
                <div className="flex justify-between pt-2">
                  <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={() => setStep('gaps')}>
                    Back to gaps
                  </Button>
                  <Button
                    size="sm"
                    className="h-9 type-meta font-bold"
                    onClick={() => setStep('review')}
                    disabled={!prefsValidation.valid || siteContextHasBlockingConflicts}
                    data-testid="rerun-prefs-next"
                  >
                    Review Changes <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* ═══ Step 4: Review & Approve ═══ */}
            {step === 'review' && (
              <div className="space-y-4">
                <div className="type-meta text-text-secondary uppercase tracking-widest">
                  Confirm what's about to run, then generate.
                </div>

                {/* Config Changes — Rerun Wizard Coherence W6.
                     Surfaces BOTH project-intent deltas (workload / nuclear /
                     priority — still editable on Step 2) AND typed-preference
                     deltas (Step 3 — the source of truth for project size,
                     cooling type, timing, and the per-type knobs). Typed-pref
                     deltas are diffed against what the panel was seeded with
                     on this mount, so a change the user made to e.g.
                     powerRequirementMW on Step 3 shows up here even though it
                     has no Step-2 dropdown anymore. */}
                {(() => {
                  const origIntent = report.rerunConfig?.projectIntent;
                  const changes: { label: string; from: string; to: string }[] = [];
                  if (origIntent) {
                    const origWorkload = effectiveWorkloadProfile(origIntent);
                    const newWorkload = effectiveWorkloadProfile(projectIntent);
                    if (origWorkload !== newWorkload) changes.push({
                      label: 'Workload profile',
                      from: workloadProfileLabel(origWorkload),
                      to: workloadProfileLabel(newWorkload),
                    });
                    if (origIntent.nuclearInterest !== projectIntent.nuclearInterest) changes.push({ label: 'Nuclear', from: String(origIntent.nuclearInterest ?? '—'), to: String(projectIntent.nuclearInterest ?? '—') });
                    if (origIntent.priority !== projectIntent.priority) changes.push({ label: 'Priority', from: String(origIntent.priority ?? '—'), to: String(projectIntent.priority ?? '—') });
                  }
                  for (const d of typedPrefDeltas(
                    initialEditedPrefs as Record<string, unknown>,
                    editedPrefs as Record<string, unknown>,
                  )) {
                    changes.push({ label: d.label, from: d.from, to: d.to });
                  }
                  if (changes.length === 0) return null;
                  return (
                    <div className="border border-info/40 bg-info/5 p-3 space-y-2" data-testid="rerun-review-config-changes">
                      <div className="type-body font-bold uppercase tracking-widest text-info">Configuration Changes</div>
                      {changes.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 type-meta">
                          <span className="text-text-secondary w-28 shrink-0">{c.label}:</span>
                          <span className="text-danger line-through">{c.from}</span>
                          <span className="text-text-secondary">→</span>
                          <span className="text-success font-bold">{c.to}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Document Summary */}
                <div className="border border-border p-3 space-y-2">
                  <div className="type-body font-bold uppercase tracking-widest text-text-secondary">Documents</div>
                  <div className="grid grid-cols-3 gap-2 type-meta text-center">
                    <div className="border border-border p-2">
                      <div className="text-lg font-bold text-text-primary">{uploadedDocs.length - excludedDocIds.size}</div>
                      <div className="type-meta text-text-secondary">Included</div>
                    </div>
                    <div className="border border-border p-2">
                      <div className="text-lg font-bold text-warning">{uploadedDocs.filter(d => d.priority && !excludedDocIds.has(d.id)).length}</div>
                      <div className="type-meta text-text-secondary">Priority (NB)</div>
                    </div>
                    <div className="border border-border p-2">
                      <div className="text-lg font-bold text-text-secondary">{excludedDocIds.size}</div>
                      <div className="type-meta text-text-secondary">Excluded</div>
                    </div>
                  </div>
                  {(() => {
                    const origDocCount = (report.documents || []).length;
                    const newDocs = uploadedDocs.filter(d => !(report.documents || []).some(rd => rd.id === d.id));
                    if (newDocs.length > 0) return (
                      <div className="type-meta text-success">
                        + {newDocs.length} new document(s): {newDocs.map(d => d.fileName).join(', ')}
                      </div>
                    );
                    return null;
                  })()}
                </div>

                {/* Gaps Summary */}
                {gaps.length > 0 && (
                  <div className="border border-border p-3 space-y-1">
                    <div className="type-body font-bold uppercase tracking-widest text-text-secondary">Data Gaps</div>
                    <div className="flex gap-3 type-meta">
                      <span className="text-success">{gaps.filter(g => g.resolved).length} resolved</span>
                      <span className="text-text-secondary">{gaps.filter(g => g.ignored).length} ignored</span>
                      <span className="text-warning">{gaps.filter(g => !g.resolved && !g.ignored).length} unresolved</span>
                    </div>
                  </div>
                )}

                {/* Pending SED Amendments */}
                {(() => {
                  const pending = useStore.getState().amendmentState?.amendments?.filter(a => a.status === 'pending') || [];
                  if (pending.length === 0) return null;
                  return (
                    <div className="border border-warning/40 bg-warning/15/30 p-3 space-y-2">
                      <div className="type-body font-bold uppercase tracking-widest text-warning">
                        Pending SED Amendments ({pending.length})
                      </div>
                      <div className="type-meta text-warning">
                        These amendments will be applied in the next rerun.
                      </div>
                      {pending.slice(0, 5).map(a => (
                        <div key={a.id} className="flex items-center gap-2 type-meta">
                          <span className="text-text-secondary w-24 truncate shrink-0">{a.variableLabel}:</span>
                          <span className="text-danger line-through">{String(a.oldValue)}</span>
                          <span className="text-text-secondary">→</span>
                          <span className="text-success font-bold">{String(a.newValue)}</span>
                        </div>
                      ))}
                      {pending.length > 5 && <div className="type-meta text-warning">+{pending.length - 5} more</div>}
                    </div>
                  );
                })()}

                {/* RR5-4 — version-note input lives on the Confirm step
                     now (relocated from the Configuration step). It's a
                     commit-message presentationally, so it belongs at
                     the point of commit, not in the middle of editing.
                     The auto-note preview chip moves with it. */}
                <div className="space-y-1" data-testid="rerun-confirm-version-note-block">
                  <label
                    htmlFor="rerun-version-note"
                    className="type-body font-bold uppercase tracking-widest text-text-secondary"
                  >
                    Why are you rerunning? <span className="text-text-muted">(optional, max 200 chars)</span>
                  </label>
                  <input
                    id="rerun-version-note"
                    type="text"
                    maxLength={200}
                    value={versionNote}
                    onChange={e => setVersionNoteFromUser(e.target.value)}
                    placeholder="e.g., Utility confirmed substation upgrade Q4 2026"
                    className="w-full text-[10px] px-3 py-2 border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                    data-testid="rerun-version-note"
                  />
                  <div className="flex items-center justify-between type-meta text-text-secondary">
                    <span>Auto-note: {generateAutoVersionNote()}</span>
                    <span className={versionNote.length > 180 ? 'text-warning' : ''}>{versionNote.length}/200</span>
                  </div>
                </div>

                {/* Pre-rerun freshness list (Reader Polish Prompt 8).
                     The shared PreflightFreshnessCard's two-button
                     surface is replaced here by a list-only widget +
                     a single "Skip refresh →" text link. The actual
                     decision is then driven by the footer CTA below
                     whose label flips based on `skipRefresh`. */}
                {!reviewSedLoading && (() => {
                  const allStatuses = computeSedSectionStatuses(reviewSed);
                  const required = (REPORT_TYPE_SED_REQUIREMENTS[report.reportType ?? 'feasibility'] ?? []) as readonly string[];
                  const requiredStale = allStatuses.filter(s => required.includes(s.id) && s.staleness !== 'fresh');
                  // Apply-on-Rerun pack (D6) — the parent report's
                  // `apply`-resolved sanity findings preview as a cyan
                  // card above the freshness list.
                  const queuedApplyFindings = (report.sanityFindings ?? []).filter(f => f.resolution === 'apply');
                  return (
                    <>
                      {/* FF1 — informational chip when the site's
                          current SED has diverged from the SED this
                          report was generated against. Tells the user
                          that freshness (and the next rerun) reads the
                          CURRENT site SED, not the pinned `report.sedId`. */}
                      {sedIdDiverged && (
                        <div
                          className="type-body text-text-muted mb-2 italic"
                          data-testid="rerun-freshness-site-sed-chip"
                        >
                          Freshness checked against the site's current SED
                          {typeof owningSite?.sedVersion === 'number' ? ` (v${owningSite.sedVersion})` : ''};
                          this report was generated against an older SED. Rerunning uses the current one.
                        </div>
                      )}
                      <RerunFreshnessList
                        staleStatuses={requiredStale}
                        allStatuses={allStatuses}
                        reportType={report.reportType ?? 'feasibility'}
                        skipRefresh={skipRefresh}
                        onToggleSkip={() => setSkipRefresh(s => !s)}
                        queuedApplyFindings={queuedApplyFindings}
                      />
                    </>
                  );
                })()}

                {/* Footer (Reader Polish Prompt 8).
                     Single primary CTA — label flips based on (a)
                     whether stale required sections exist and (b)
                     whether the user opted to skip refresh. The
                     previous three-button mess (REFRESH & GENERATE,
                     GENERATE WITHOUT REFRESH, GENERATE V2) collapses
                     into this one decision. Icon swapped from the
                     RefreshCw used elsewhere to Zap so different
                     actions don't share an icon. */}
                {(() => {
                  const allStatuses = computeSedSectionStatuses(reviewSed);
                  const required = (REPORT_TYPE_SED_REQUIREMENTS[report.reportType ?? 'feasibility'] ?? []) as readonly string[];
                  const requiredStale = allStatuses.filter(s => required.includes(s.id) && s.staleness !== 'fresh');
                  const willRefresh = requiredStale.length > 0 && !skipRefresh;
                  const nextVersion = (report.version || 1) + 1;
                  const ctaLabel = willRefresh
                    ? `Refresh stale evidence & Generate v${nextVersion}`
                    : skipRefresh && requiredStale.length > 0
                      ? `Generate v${nextVersion} (without refreshing)`
                      : `Generate v${nextVersion}`;
                  const handleClick = () => {
                    if (willRefresh) {
                      void handleRerunWithRefresh(requiredStale.map(s => s.id));
                    } else if (skipRefresh && requiredStale.length > 0) {
                      handleRerunSkipRefresh(requiredStale.map(s => s.id));
                    } else {
                      void handleRerunRequest();
                    }
                  };
                  return (
                    <div className="flex justify-between items-end pt-2">
                      <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={() => setStep('prefs')}>
                        Back
                      </Button>
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          size="sm"
                          className="h-9 type-meta font-bold"
                          onClick={handleClick}
                          disabled={isRunning || isPreflightRefreshing || !prefsValidation.valid}
                          title={!prefsValidation.valid
                            ? `Missing required: ${prefsValidation.missingRequired.join(', ')}`
                            : undefined}
                          data-testid="rerun-generate"
                        >
                          <Zap className={`w-3.5 h-3.5 mr-2 ${isRunning ? 'animate-pulse' : ''}`} aria-hidden="true" />
                          {ctaLabel}
                        </Button>
                        {willRefresh && (
                          <span className="type-body text-text-muted" data-testid="rerun-cta-subtitle">
                            Adds ~60s · no extra cost
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
              </>
            )}
          </div>
        )}
      </div>{/* close main column */}
      {/* RP2 — the right-hand "Changes for v1 → v2" diff sidebar (RR5)
           has been deleted. Its low-info-density single-line summary +
           buried "Save & exit" link earned a full column of real estate
           for almost no information. The diff is now inline on the Run
           CTA via RerunDiffChip (RP1); Save & exit is promoted to a
           peer button beside Run in the happy-path + Confirm action
           footers below. */}
      </div>{/* close modal panel (bg-background flex) */}
    </div>{/* close modal backdrop */}
    {keyDialog && (
      <TestAndLinkKeyDialog
        provider={keyDialog.provider}
        open
        onClose={() => setKeyDialog(null)}
        onLinked={() => {
          const { provider, gapIdx } = keyDialog;
          setGaps(prev => prev.map((g, i) => i === gapIdx
            ? { ...g, resolved: true, resolution: `${provider.displayName} key linked. Click Rerun to apply.` }
            : g));
          toast.success('Key linked. Click Rerun to apply.');
        }}
      />
    )}
    {/* SC2-6 — Apply-to-site confirmation modal. Surfaces the changes
         the user is about to commit before they hit Firestore + every
         future report on this site. Cancelling aborts the rerun (does
         NOT silently downgrade to override-only). */}
    {isApplyConfirmOpen && (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
        data-testid="rerun-apply-to-site-modal"
      >
        <div className="bg-background max-w-md w-full mx-4 shadow-2xl border border-border">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-bold uppercase tracking-widest text-text-primary">
              Apply these changes to the site?
            </h3>
          </div>
          <div className="px-5 py-4 space-y-3 text-[12px]">
            <p className="text-text-secondary leading-relaxed">
              These edits will be written to <span className="font-semibold text-text-primary">{report.siteName}</span>.
              All future reports on this site will inherit them.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-text-primary">
              <li>Project Intent edits (saved to <code>site.projectIntent</code>)</li>
              <li>{REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility']?.shortLabel ?? 'Report'} Preferences (saved to <code>site.typedPreferencesDefaults</code>)</li>
            </ul>
            <p className="text-[11px] text-text-muted italic">
              Cancel to abort — switch to "Override for this rerun only" if you want one-off edits.
            </p>
          </div>
          <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
            <Button
              variant="outline"
              className="h-8 rounded-md type-meta"
              onClick={handleApplyToSiteCancel}
              data-testid="rerun-apply-to-site-cancel"
            >
              Cancel
            </Button>
            <Button
              className="h-8 rounded-md type-meta"
              onClick={() => void handleApplyToSiteConfirm()}
              data-testid="rerun-apply-to-site-confirm"
            >
              Apply &amp; generate
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* RP2 — in-place data-gap resolve dialog. Routes the constructed
         SEDAmendment through the ONE persist path; on success the gap is marked
         resolved with the real applied value (not the old placeholder note). */}
    <ResolveAmendmentDialog
      open={resolveDraft != null}
      draft={resolveDraft}
      onResolve={(amendment) => saveSEDAmendment(amendment)}
      onClose={() => {
        setResolveDraft(null);
        setResolveGapIdx(null);
      }}
      onResolved={(amendment) => {
        const idx = resolveGapIdx;
        if (idx != null) {
          setGaps((prev) =>
            prev.map((g, i) =>
              i === idx
                ? { ...g, resolved: true, resolution: `Set to ${String(amendment.newValue)} via site evidence` }
                : g,
            ),
          );
        }
        toast.success('Data gap resolved at the value source.');
      }}
      toast={(msg) => toast.info(msg)}
    />
    </>
  );
}

/* RerunTip extracted into the shared GenerationProgress component
   (Workflow Prompt 5) so the wizard and the rerun panel render the
   same cycling-tip behaviour. */
